import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LeadSource,
  GoogleCalendarConfig,
  GoogleCalendarList,
  OnboardingTemplateItem,
  ProjectDepartment,
  CUSTOM_FRAME_DEPARTMENTS,
  DEPARTMENT_DISPLAY_LABELS,
} from '../../types';
import type { SettingsSectionId } from './settingsNav';
import { useCRM } from '../../utils/CRMContext';
import { useAuth } from '../../utils/AuthContext';
import { getSourceColors, setSourceColor, resetSourceColor, resetSourceColors, DEFAULT_SOURCE_COLORS } from '../../utils/sourceColors';
import { formatSourceName } from '../../utils/sourceUtils';
import { metricsLiteClient } from '../../utils/metricsLite';
import { getMetricsLiteApiKey, saveMetricsLiteApiKey, getCrmApiKey, saveCrmApiKey, getAgentApiKey, saveAgentApiKey, saveBiddingAgentIdentity, getSendblueApiKey, saveSendblueApiKey, getSendblueApiSecret, saveSendblueApiSecret, getSlackSettings, saveSlackSettings, subscribeToSettings, initializeSettings, getSettings, updateSetting, getProposalRates, saveProposalRates, ProposalRateSettings, DEFAULT_PROPOSAL_RATES, mergePublicSettingsSubset, getSequenceSettings, saveSequenceSettings, getSequenceSyncMeta, SequenceSyncMeta } from '../../utils/appSettings';
import { AGENT_CATALOG_JSON_URL } from '../../utils/agentRegistryGuide';
import CompanyBrainRepoCard from '../skillsAtlas/CompanyBrainRepoCard';
import { ensurePortalPublicBrandingMediaItem } from '../../utils/portalBrandingStorage';
import { db } from '../../utils/firebase';
import { doc, setDoc, getDoc, updateDoc, collection, onSnapshot, query } from 'firebase/firestore';
import { qbExpensesAccountDiscoveryQuery } from '../../utils/qbExpensesQueries';
import { testSendblueConnection, sendSMS } from '../../utils/sendblue';
import { crmAuthedFetch } from '../../utils/crmAuthedFetch';
import { testSequenceConnection, syncSequenceCards, syncSequenceTransactions, SEQUENCE_AUTH0_CLIENT_ID } from '../../utils/sequenceApi';
import ExpenseGroupManagerModal from './ExpenseGroupManagerModal';
import {
  countLegendMappedAccounts,
  EXPENSE_LEGEND_DOC,
  fetchExpenseLegend,
  groupsMissingAccounts,
  type ExpenseLegendRow,
} from '../../utils/expenseLegend';
import DebitCardSettingsPanel from '../sales-activity/DebitCardSettingsPanel';
import {
  backfillShortTermOpsAssignee,
  DEFAULT_CARD_EXPENSE_AUTO_RULES,
  getCardExpensePolicy,
  saveCardExpenseAutoAssignRules,
  saveCardExpenseDigestFrequency,
  saveShortTermOpsAssignee,
  type CardExpenseAutoRule,
  type CardExpenseDigestFrequency,
} from '../../utils/cardExpensePolicy';
import {
  getUserGoogleCalendarConfig,
  getGoogleAuthUrl,
  disconnectGoogleCalendar,
  listCalendars,
  updateSelectedCalendars,
  getGoogleClientId,
  setGoogleClientId,
} from '../../utils/googleCalendarService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { subscribeToTimberItems } from '../../utils/materialsDatabaseService';
import UserManagement from '../admin/UserManagement';
import QuestionnaireSettingsTab from './QuestionnaireSettingsTab';
import CrewBoardSettingsCard from './CrewBoardSettingsCard';
import EngineeringFirmsSettings from './EngineeringFirmsSettings';
import ProjectNumberingSettings from './ProjectNumberingSettings';
import PeopleHubBenefitsAdminSettings from './PeopleHubBenefitsAdminSettings';
import { MediaPicker } from '../media/MediaPicker';
import { MediaItem } from '../../types';
import {
  Users2, Lock, Activity, FileText, Upload, X, Image, Box, Trash2,
  CheckCircle2, AlertTriangle, Loader2, Sparkles, Eye, EyeOff,
  ChevronDown, ChevronRight, GripVertical, Plus, ArrowUp, ArrowDown,
  Copy, MessageSquare, Calendar, DollarSign, Phone,
  Wrench, Smartphone, BarChart3, Inbox, MapPin, Briefcase, Palette, Calculator, ListTodo,
} from 'lucide-react';
import PaymentTermsSettingsCard from './PaymentTermsSettingsCard';
import GroupTimelineColorsSettingsCard from './GroupTimelineColorsSettingsCard';
import PurchasingPolicySettingsCard from './PurchasingPolicySettingsCard';
import DesignerStandardsSettingsCard from './DesignerStandardsSettingsCard';
import FilesRedesignPilotCard from './FilesRedesignPilotCard';
import RfqRequestsPilotCard from './RfqRequestsPilotCard';
import './settings-hub-forms.css';

const AGENT_GATEWAY_ENDPOINT = 'https://us-central1-lctf-projects.cloudfunctions.net/agentGatewayApi';

const PORTAL_ACTIVITY_AGENT_ENDPOINT = 'https://us-central1-lctf-projects.cloudfunctions.net/portalActivityAgentApi';
const OPS_TASKS_AGENT_ENDPOINT = 'https://us-central1-lctf-projects.cloudfunctions.net/opsTasksAgentApi';

interface AutomationSettings {
  autoCreateInitialCall: boolean;
  firstCallInterval: number;
  secondCallInterval: number;
  thirdCallInterval: number;
  fourthCallInterval: number;
  maxAttempts: number;
  workingHoursStart: number;
  workingHoursEnd: number;
  workingDays: number[];
  autoMarkLost: boolean;
}

interface SettingsHubSectionProps {
  section: SettingsSectionId;
}

export const SettingsHubSection: React.FC<SettingsHubSectionProps> = ({ section }) => {
  const location = useLocation();
  const [expandedIntegrations, setExpandedIntegrations] = useState<Record<string, boolean>>({});
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [inlineFeedback, setInlineFeedback] = useState<string | null>(null);

  const showCopyFeedback = (label: string) => {
    setCopyFeedback(label);
    window.setTimeout(() => setCopyFeedback(null), 2000);
  };
  const showInlineFeedback = (label: string) => {
    setInlineFeedback(label);
    window.setTimeout(() => setInlineFeedback(null), 3000);
  };
  const toggleIntegration = (id: string) => {
    setExpandedIntegrations(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    if (section !== 'integrations') return;
    const hash = location.hash.replace(/^#/, '');
    if (hash === 'sequence-cards' || hash === 'sequence-sto' || hash === 'sequence') {
      setExpandedIntegrations(prev => ({ ...prev, sequence: true }));
      window.requestAnimationFrame(() => {
        document.getElementById(hash === 'sequence-sto' ? 'sequence-sto' : 'sequence-cards')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  }, [section, location.hash]);

  // Call automation settings
  const [automationSettings, setAutomationSettings] = useState<AutomationSettings>(() => {
    const saved = localStorage.getItem('crm_automation_settings');
    if (saved) {
      try {
        return JSON.parse(saved) as AutomationSettings;
      } catch (e) { }
    }
    return {
      autoCreateInitialCall: true,
      firstCallInterval: 1, // hours after lead creation
      secondCallInterval: 2, // hours after first attempt
      thirdCallInterval: 24, // hours (1 day)
      fourthCallInterval: 72, // hours (3 days)
      maxAttempts: 4,
      workingHoursStart: 9, // 9 AM
      workingHoursEnd: 17, // 5 PM
      workingDays: [1, 2, 3, 4, 5], // Mon-Fri
      autoMarkLost: true, // Auto prompt to mark as lost after max attempts
    };
  });

  // Metrics Lite settings (only API key is configurable, URL is hardcoded)
  const [metricsLiteSettings, setMetricsLiteSettings] = useState({
    apiUrl: 'https://app.metricslite.com/api/v1', // Hardcoded
    apiKey: '',
    testLeadId: localStorage.getItem('metrics_lite_test_lead_id') || '34502', // Default test lead ID
  });
  const [metricsLiteTesting, setMetricsLiteTesting] = useState(false);

  // Sendblue settings
  const [sendblueSettings, setSendblueSettings] = useState({
    apiKey: '',
    apiSecret: '',
  });
  const [sendblueSaving, setSendblueSaving] = useState(false);
  const [sendblueSaveStatus, setSendblueSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [sendblueTesting, setSendblueTesting] = useState(false);
  const [sendblueTestResult, setSendblueTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [sendblueTestNumber, setSendblueTestNumber] = useState('');
  const [sequenceSettings, setSequenceSettings] = useState({
    enabled: false,
    accessToken: '',
    graphqlAccessToken: '',
    refreshToken: '',
    auth0ClientId: SEQUENCE_AUTH0_CLIENT_ID,
    organizationId: '',
  });
  const [sequenceSyncMeta, setSequenceSyncMeta] = useState<SequenceSyncMeta>({});
  const [sequenceSaving, setSequenceSaving] = useState(false);
  const [sequenceSaveStatus, setSequenceSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [sequenceTesting, setSequenceTesting] = useState(false);
  const [sequenceTestResult, setSequenceTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [sequenceSyncing, setSequenceSyncing] = useState<'cards' | 'transactions' | null>(null);
  const [shortTermOpsAssigneeUserId, setShortTermOpsAssigneeUserId] = useState('');
  const [shortTermOpsAssigneeSaving, setShortTermOpsAssigneeSaving] = useState(false);
  const [shortTermOpsAssigneeBackfilling, setShortTermOpsAssigneeBackfilling] = useState(false);
  const [shortTermOpsAssigneeStatus, setShortTermOpsAssigneeStatus] = useState<string | null>(null);
  const [cardExpenseDigestFrequency, setCardExpenseDigestFrequency] =
    useState<CardExpenseDigestFrequency>('weekly');
  const [cardExpenseDigestSaving, setCardExpenseDigestSaving] = useState(false);
  const [cardExpenseDigestStatus, setCardExpenseDigestStatus] = useState<string | null>(null);
  const [cardExpenseAutoRules, setCardExpenseAutoRules] = useState<CardExpenseAutoRule[]>(
    DEFAULT_CARD_EXPENSE_AUTO_RULES,
  );
  const [cardExpenseAutoRulesSaving, setCardExpenseAutoRulesSaving] = useState(false);
  const [cardExpenseAutoRulesStatus, setCardExpenseAutoRulesStatus] = useState<string | null>(null);
  const [metricsLiteTestResult, setMetricsLiteTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [savingApiKey, setSavingApiKey] = useState(false);

  // Slack settings
  const [slackSettings, setSlackSettings] = useState({ botToken: '', channelId: '' });
  const [slackSaving, setSlackSaving] = useState(false);
  const [slackSaveStatus, setSlackSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  // Agent gateway settings
  const [agentApiKey, setAgentApiKey] = useState('');
  const [savingAgentApiKey, setSavingAgentApiKey] = useState(false);
  const [revealAgentApiKey, setRevealAgentApiKey] = useState(false);
  const [agentGatewayTestResult, setAgentGatewayTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [agentGatewayTesting, setAgentGatewayTesting] = useState(false);
  const [biddingAgentUserId, setBiddingAgentUserId] = useState('');
  const [biddingAgentDisplayName, setBiddingAgentDisplayName] = useState('');
  const [savingBiddingAgentIdentity, setSavingBiddingAgentIdentity] = useState(false);
  const [agentCatalogInfo, setAgentCatalogInfo] = useState<{ version?: string; hash?: string; apis?: string[] } | null>(null);
  const [agentCatalogLoading, setAgentCatalogLoading] = useState(false);

  // CRM Lead API settings
  const [crmApiKey, setCrmApiKey] = useState('');
  const [savingCrmApiKey, setSavingCrmApiKey] = useState(false);
  const [revealCrmApiKey, setRevealCrmApiKey] = useState(false);

  // Google Calendar settings
  const { user } = useAuth();
  const [googleCalendarConfig, setGoogleCalendarConfig] = useState<GoogleCalendarConfig | null>(null);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendarList[]>([]);
  const [googleCalendarLoading, setGoogleCalendarLoading] = useState(false);
  const [googleCalendarError, setGoogleCalendarError] = useState<string | null>(null);
  const [googleClientId, setGoogleClientIdState] = useState('');
  const [savingGoogleClientId, setSavingGoogleClientId] = useState(false);

  // QuickBooks settings
  const [quickbooksSettings, setQuickbooksSettings] = useState({
    clientId: '',
    clientSecret: '',
    enabled: false,
    connected: false,
    realmId: '',
    paymentNotifyUsers: [] as string[],
    paymentMoveToStage: '',
    paymentLink: '',
    paymentRequestedStage: '',
    paymentLinks: [] as { id: string; name: string; url: string }[],
  });
  const [quickbooksSaving, setQuickbooksSaving] = useState(false);
  const [quickbooksSaveStatus, setQuickbooksSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [qbConnecting, setQbConnecting] = useState(false);
  const [qbConnectError, setQbConnectError] = useState<string | null>(null);
  const [expenseCategories, setExpenseCategories] = useState<{ overhead: string[]; direct: string[] }>({ overhead: [], direct: [] });
  const [allExpenseAccounts, setAllExpenseAccounts] = useState<string[]>([]);
  const [expenseCatSaving, setExpenseCatSaving] = useState(false);
  const [expenseGroupsModalOpen, setExpenseGroupsModalOpen] = useState(false);
  const [expenseLegendRows, setExpenseLegendRows] = useState<ExpenseLegendRow[]>([]);
  const [expenseLegendUpdatedAt, setExpenseLegendUpdatedAt] = useState<Date | null>(null);
  const manageGroupsBtnRef = useRef<HTMLButtonElement>(null);

  // QB Invoice mapping state
  const [qbItems, setQbItems] = useState<Array<{ qbId: string; name: string; type: string; description?: string; active: boolean }>>([]);
  const [qbItemsSyncing, setQbItemsSyncing] = useState(false);
  const [qbInvoiceMapping, setQbInvoiceMapping] = useState({
    enabled: false,
    autoCreateOnSign: false,
    autoSendEmail: false,
    designIncludedInKit: false,
    designItemId: '',
    designItemName: '',
    timberKitItemId: '',
    timberKitItemName: '',
    timberKitSpeciesMap: {} as Record<string, { id: string; name: string }>,
    installItemId: '',
    installItemName: '',
    paymentTermsDays: 30,
    termsRef: '',
  });
  const [newSpeciesName, setNewSpeciesName] = useState('');
  const [availableSpecies, setAvailableSpecies] = useState<string[]>([]);
  const [qbMappingSaving, setQbMappingSaving] = useState(false);
  const [qbMappingSaveStatus, setQbMappingSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  // Quick Links settings (booking link, etc.)
  const [bookingLink, setBookingLink] = useState('');
  const [handCutVsCncComparisonUrl, setHandCutVsCncComparisonUrl] = useState('');
  const [handCutVsCncUrlSaving, setHandCutVsCncUrlSaving] = useState(false);

  // Docs settings for engagement letters
  const [engagementLogo, setEngagementLogo] = useState<{ url: string; name: string } | null>(null);
  const [engagementBackground, setEngagementBackground] = useState<{ url: string; name: string } | null>(null);
  const [engagement3DModel, setEngagement3DModel] = useState<{ url: string; name: string } | null>(null);
  const [samplePlansImage, setSamplePlansImage] = useState<{ url: string; name: string } | null>(null);
  const [engagementDocuments, setEngagementDocuments] = useState<Array<{ id: string; name: string; url: string }>>([]);
  const [engagementVideoUrl, setEngagementVideoUrl] = useState('');
  const [retainerTotalFeeRange, setRetainerTotalFeeRange] = useState('$4,500 - $6,000');
  const [conceptPaymentLinkId, setConceptPaymentLinkId] = useState('');
  const [retainerPaymentLinkId, setRetainerPaymentLinkId] = useState('');
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [showSamplePlansPicker, setShowSamplePlansPicker] = useState(false);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [savingDocsSettings, setSavingDocsSettings] = useState(false);

  // Portal appearance settings
  const [portalOverlayColor, setPortalOverlayColor] = useState('#ffffff');
  const [portalOverlayOpacity, setPortalOverlayOpacity] = useState(0.92);
  const [portalBackgroundImage, setPortalBackgroundImage] = useState<{ url: string; name: string } | null>(null);
  const [showPortalBgPicker, setShowPortalBgPicker] = useState(false);
  const [modelWalkthroughDefaultEnabled, setModelWalkthroughDefaultEnabled] = useState(false);
  const [savingWalkthroughDefault, setSavingWalkthroughDefault] = useState(false);
  const [phasedProjectsDefaultEnabled, setPhasedProjectsDefaultEnabled] = useState(false);
  const [savingPhasedProjectsDefault, setSavingPhasedProjectsDefault] = useState(false);

  // Batch geocode state
  const [geocodeRunning, setGeocodeRunning] = useState(false);
  const [geocodeResult, setGeocodeResult] = useState<{ needed: number; geocoded: number; failed: number; total: number } | null>(null);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);

  // AI/Bidding settings
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [anthropicApiKey, setAnthropicApiKey] = useState('');
  const [savingAiKeys, setSavingAiKeys] = useState(false);
  const [aiKeysSaveStatus, setAiKeysSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [showOpenaiKey, setShowOpenaiKey] = useState(false);
  const [showAnthropicKey, setShowAnthropicKey] = useState(false);

  // Proposal rates settings
  const [proposalRates, setProposalRates] = useState<ProposalRateSettings>(DEFAULT_PROPOSAL_RATES);
  const [savingProposalRates, setSavingProposalRates] = useState(false);
  const [proposalRatesSaveStatus, setProposalRatesSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  // Design fee pricing settings
  const [feeServices, setFeeServices] = useState<{ id: string; name: string; price: number; description: string; required?: boolean }[]>([
    { id: '3d-model', name: '3D Concept Model', price: 1300, description: 'We need this to quote your project — you\'ll get an interactive 3D model you can rotate, zoom into, and explore from every angle.', required: true },
    { id: '2d-elevations', name: '2D Elevation Drawings', price: 850, description: 'Printable 2D plans of your frame. Not required for your quote, but recommended for complex projects where your builder or engineer needs detailed elevation references.' },
    { id: 'floor-plan', name: 'Floor Plan', price: 750, description: 'A layout of the room and space around your frame. Not required for your quote, but helpful if you want to plan furniture placement and visualize how the space will flow.' },
  ]);
  const [savingFees, setSavingFees] = useState(false);
  const [feeSaveStatus, setFeeSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  // Project Timeline defaults
  const DEFAULT_TIMELINE_PHASES = [
    { id: '2d-plans', label: 'Engineered Drawings', durationWeeks: 3, bufferWeeks: 1 },
    { id: 'in-production', label: 'Production', durationWeeks: 8, bufferWeeks: 1, subPhases: [
      { id: 'material-lead', label: 'Material Lead Time', durationWeeks: 6 },
      { id: 'cnc-production', label: 'CNC Production', durationWeeks: 2 },
    ] },
    { id: 'delivery', label: 'Delivery & Install', durationWeeks: 2, bufferWeeks: 0 },
  ];
  const [timelinePhases, setTimelinePhases] = useState(DEFAULT_TIMELINE_PHASES);
  const [savingTimeline, setSavingTimeline] = useState(false);
  const [timelineSaveStatus, setTimelineSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [expandedProductionRow, setExpandedProductionRow] = useState(false);

  // Onboarding checklist template
  const [onboardingItems, setOnboardingItems] = useState<OnboardingTemplateItem[]>([]);
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [onboardingSaveStatus, setOnboardingSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  // Load API keys from Firestore on mount
  useEffect(() => {
    const loadApiKeys = async () => {
      try {
        const metricsKey = await getMetricsLiteApiKey();
        setMetricsLiteSettings(prev => ({ ...prev, apiKey: metricsKey }));

        const slack = await getSlackSettings();

        const agentKey = await getAgentApiKey();
        setAgentApiKey(agentKey);

        const crmKey = await getCrmApiKey();
        setCrmApiKey(crmKey);

        setSlackSettings({ botToken: slack.botToken, channelId: slack.channelId });





        // Load Sendblue credentials
        const sendblueKey = await getSendblueApiKey();
        const sendblueSecret = await getSendblueApiSecret();
        setSendblueSettings({ apiKey: sendblueKey, apiSecret: sendblueSecret });

        const seq = await getSequenceSettings();
        setSequenceSettings({
          ...seq,
          auth0ClientId: seq.auth0ClientId || SEQUENCE_AUTH0_CLIENT_ID,
        });
        const seqMeta = await getSequenceSyncMeta();
        setSequenceSyncMeta(seqMeta);
        const cardPolicy = await getCardExpensePolicy();
        setShortTermOpsAssigneeUserId(cardPolicy.shortTermOpsAssigneeUserId || '');
        setCardExpenseDigestFrequency(cardPolicy.digestFrequency || 'weekly');
        setCardExpenseAutoRules(cardPolicy.autoAssignRules || DEFAULT_CARD_EXPENSE_AUTO_RULES);

        // Load QuickBooks settings
        const allSettings = await getSettings();
        if (allSettings) {
          setQuickbooksSettings({
            clientId: allSettings.quickbooksClientId || '',
            clientSecret: allSettings.quickbooksClientSecret || '',
            enabled: allSettings.quickbooksEnabled || false,
            connected: !!(allSettings.quickbooksAccessToken && allSettings.quickbooksRealmId),
            realmId: allSettings.quickbooksRealmId || '',
            paymentNotifyUsers: allSettings.quickbooksPaymentNotifyUsers || [],
            paymentMoveToStage: allSettings.quickbooksPaymentMoveToStage || '',
            paymentLink: allSettings.quickbooksPaymentLink || '',
            paymentRequestedStage: allSettings.quickbooksPaymentRequestedStage || '',
            paymentLinks: allSettings.quickbooksPaymentLinks || [],
          });
          // Load booking link
          setBookingLink(allSettings.bookingLink || '');
          setHandCutVsCncComparisonUrl(allSettings.handCutVsCncComparisonUrl || '');

          // Load docs settings
          if (allSettings.engagementLogo) {
            setEngagementLogo(allSettings.engagementLogo);
          }
          if (allSettings.engagementBackground) {
            setEngagementBackground(allSettings.engagementBackground);
          }
          if (allSettings.engagement3DModel) {
            setEngagement3DModel(allSettings.engagement3DModel);
          }
          if (allSettings.samplePlansImage) {
            setSamplePlansImage(allSettings.samplePlansImage);
          }
          if (Array.isArray(allSettings.engagementDocuments)) {
            setEngagementDocuments(allSettings.engagementDocuments);
          }
          if (allSettings.engagementVideoUrl) {
            setEngagementVideoUrl(allSettings.engagementVideoUrl);
          }
          if (allSettings.retainerTotalFeeRange) {
            setRetainerTotalFeeRange(allSettings.retainerTotalFeeRange);
          }

          if (allSettings.conceptPaymentLinkId) {
            setConceptPaymentLinkId(allSettings.conceptPaymentLinkId);
          }
          if (allSettings.retainerPaymentLinkId) {
            setRetainerPaymentLinkId(allSettings.retainerPaymentLinkId);
          }
          if (typeof allSettings.modelWalkthroughDefaultEnabled === 'boolean') {
            setModelWalkthroughDefaultEnabled(allSettings.modelWalkthroughDefaultEnabled);
          }
          if (typeof allSettings.phasedProjectsDefaultEnabled === 'boolean') {
            setPhasedProjectsDefaultEnabled(allSettings.phasedProjectsDefaultEnabled);
          }

          // Load portal appearance settings
          if (allSettings.portalOverlayColor) {
            setPortalOverlayColor(allSettings.portalOverlayColor);
          }
          if (typeof allSettings.portalOverlayOpacity === 'number') {
            setPortalOverlayOpacity(allSettings.portalOverlayOpacity);
          }
          if (allSettings.portalBackgroundImage) {
            setPortalBackgroundImage(allSettings.portalBackgroundImage);
          }

          // Load AI/Bidding settings from main settings
          if (allSettings.geminiApiKey) {
            setGeminiApiKey(allSettings.geminiApiKey);
          }
        }

        // Also load from appSettings/aiModels (where AI keys are stored)
        try {
          const aiModelsSnap = await getDoc(doc(db, 'appSettings', 'aiModels'));
          if (aiModelsSnap.exists()) {
            const aiData = aiModelsSnap.data();
            if (aiData.geminiApiKey) setGeminiApiKey(aiData.geminiApiKey);
            if (aiData.openaiApiKey) setOpenaiApiKey(aiData.openaiApiKey);
            if (aiData.anthropicApiKey) setAnthropicApiKey(aiData.anthropicApiKey);
          }
        } catch (e) {
          console.error('Error loading AI model keys:', e);
        }

        // Load proposal rates
        const rates = await getProposalRates();
        setProposalRates(rates);

        // Load design fee pricing
        try {
          const feeSnap = await getDoc(doc(db, 'appSettings', 'designFeePricing'));
          if (feeSnap.exists()) {
            const fData = feeSnap.data();
            if (fData.services?.length) setFeeServices(fData.services);
          }
        } catch { /* use defaults */ }

        // Load project timeline defaults
        try {
          const allSettings = await getSettings();
          if (allSettings.projectTimelineDefaults?.phases?.length) {
            setTimelinePhases(allSettings.projectTimelineDefaults.phases);
          }
        } catch { /* use defaults */ }

        // Load onboarding checklist template
        try {
          const obSnap = await getDoc(doc(db, 'settings', 'onboardingChecklist'));
          if (obSnap.exists()) {
            const obData = obSnap.data();
            if (obData.items?.length) setOnboardingItems(obData.items);
          }
        } catch { /* use defaults */ }
      } catch (error) {
        console.error('Error loading API keys:', error);
      }
    };

    loadApiKeys();

    // Subscribe to settings changes
    const unsubscribe = subscribeToSettings((settings) => {
      if (settings.agentApiKey !== undefined) {
        setAgentApiKey(settings.agentApiKey || '');
      }
      if (settings.crmApiKey !== undefined) {
        setCrmApiKey(settings.crmApiKey || '');
      }
      if (settings.metricsLiteApiKey) {
        setMetricsLiteSettings(prev => ({ ...prev, apiKey: settings.metricsLiteApiKey || '' }));
      }
      if (settings.biddingAgentUserId !== undefined) {
        setBiddingAgentUserId(settings.biddingAgentUserId || '');
      }
      if (settings.biddingAgentDisplayName !== undefined) {
        setBiddingAgentDisplayName(settings.biddingAgentDisplayName || '');
      }
      if (settings.slackBotToken !== undefined || settings.slackChannelId !== undefined) {
        setSlackSettings(prev => ({
          ...prev,
          botToken: settings.slackBotToken ?? prev.botToken,
          channelId: settings.slackChannelId ?? prev.channelId,
        }));
      }
      if (settings.sendblueApiKey !== undefined || settings.sendblueApiSecret !== undefined) {
        setSendblueSettings(prev => ({
          apiKey: settings.sendblueApiKey ?? prev.apiKey,
          apiSecret: settings.sendblueApiSecret ?? prev.apiSecret,
        }));
      }
      if (settings.googleCalendarClientId !== undefined) {
        setGoogleClientIdState(settings.googleCalendarClientId || '');
      }
      // Update QuickBooks settings on change
      if (settings.quickbooksClientId !== undefined || settings.quickbooksEnabled !== undefined) {
        setQuickbooksSettings(prev => ({
          ...prev,
          clientId: settings.quickbooksClientId ?? prev.clientId,
          clientSecret: settings.quickbooksClientSecret ?? prev.clientSecret,
          enabled: settings.quickbooksEnabled ?? prev.enabled,
          connected: !!(settings.quickbooksAccessToken && settings.quickbooksRealmId),
          realmId: settings.quickbooksRealmId ?? prev.realmId,
          paymentNotifyUsers: settings.quickbooksPaymentNotifyUsers ?? prev.paymentNotifyUsers,
          paymentMoveToStage: settings.quickbooksPaymentMoveToStage ?? prev.paymentMoveToStage,
          paymentLink: settings.quickbooksPaymentLink ?? prev.paymentLink,
          paymentRequestedStage: settings.quickbooksPaymentRequestedStage ?? prev.paymentRequestedStage,
          paymentLinks: settings.quickbooksPaymentLinks ?? prev.paymentLinks,
        }));
      }
      // Update booking link
      if (settings.bookingLink !== undefined) {
        setBookingLink(settings.bookingLink || '');
      }
      if (settings.handCutVsCncComparisonUrl !== undefined) {
        setHandCutVsCncComparisonUrl(settings.handCutVsCncComparisonUrl || '');
      }
      // Update docs settings
      if (settings.engagementLogo !== undefined) {
        setEngagementLogo(settings.engagementLogo || null);
      }
      if (settings.engagementBackground !== undefined) {
        setEngagementBackground(settings.engagementBackground || null);
      }
      if (settings.engagement3DModel !== undefined) {
        setEngagement3DModel(settings.engagement3DModel || null);
      }
      if (settings.samplePlansImage !== undefined) {
        setSamplePlansImage(settings.samplePlansImage || null);
      }
      if (settings.engagementDocuments !== undefined) {
        setEngagementDocuments(settings.engagementDocuments || []);
      }
      if (settings.engagementVideoUrl !== undefined) {
        setEngagementVideoUrl(settings.engagementVideoUrl || '');
      }
      if (settings.retainerTotalFeeRange !== undefined) {
        setRetainerTotalFeeRange(settings.retainerTotalFeeRange || '$4,500 - $6,000');
      }
      if (settings.conceptPaymentLinkId !== undefined) {
        setConceptPaymentLinkId(settings.conceptPaymentLinkId || '');
      }
      if (settings.retainerPaymentLinkId !== undefined) {
        setRetainerPaymentLinkId(settings.retainerPaymentLinkId || '');
      }
    });

    return unsubscribe;
  }, []);

  // Load expense categories + discover unique account names from synced expenses
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const catDoc = await getDoc(doc(db, 'settings', 'expenseCategories'));
        if (catDoc.exists()) {
          const data = catDoc.data();
          setExpenseCategories({
            overhead: data.overhead || [],
            direct: data.direct || [],
          });
        }
      } catch (err) {
        console.error('Error loading expense categories:', err);
      }
    };
    loadCategories();

    const loadExpenseLegendSummary = async () => {
      try {
        const [legendSnap] = await Promise.all([fetchExpenseLegend()]);
        setExpenseLegendRows(legendSnap);
        const metaDoc = await getDoc(doc(db, EXPENSE_LEGEND_DOC));
        if (metaDoc.exists()) {
          const raw = metaDoc.data()?.updatedAt as { toDate?: () => Date } | undefined;
          setExpenseLegendUpdatedAt(raw?.toDate?.() || null);
        }
      } catch (err) {
        console.error('Error loading expense legend summary:', err);
      }
    };
    loadExpenseLegendSummary();

    const loadQbInvoiceMapping = async () => {
      try {
        const mapDoc = await getDoc(doc(db, 'settings', 'qbInvoiceMapping'));
        if (mapDoc.exists()) {
          const d = mapDoc.data();
          setQbInvoiceMapping(prev => ({ ...prev, ...d }));
        }
        const itemsDoc = await getDoc(doc(db, 'settings', 'quickbooksItems'));
        if (itemsDoc.exists()) {
          setQbItems(itemsDoc.data().items || []);
        }
      } catch (err) {
        console.error('Error loading QB invoice mapping:', err);
      }
    };
    loadQbInvoiceMapping();

    const unsubTimber = subscribeToTimberItems((items) => {
      const speciesSet = new Set(items.map(i => i.species));
      setAvailableSpecies(Array.from(speciesSet).sort());
    });

    const expQ = qbExpensesAccountDiscoveryQuery();
    const unsubExp = onSnapshot(expQ, (snapshot) => {
      const accountSet = new Set<string>();
      snapshot.docs.forEach(d => {
        const data = d.data();
        if (data.accountName) accountSet.add(data.accountName);
        (data.lineItems || []).forEach((li: any) => {
          if (li.accountName) accountSet.add(li.accountName);
        });
      });
      setAllExpenseAccounts(Array.from(accountSet).sort());
    });

    return () => { unsubExp(); unsubTimber(); };
  }, []);

  // Load Google Calendar config
  useEffect(() => {
    const loadGoogleCalendarConfig = async () => {
      if (!user?.id) return;

      try {
        const config = await getUserGoogleCalendarConfig(user.id);
        setGoogleCalendarConfig(config);

        // Load available calendars if connected
        if (config?.connected) {
          const calendars = await listCalendars(user.id);
          setGoogleCalendars(calendars);
        }

        // Load client ID
        const clientId = await getGoogleClientId();
        setGoogleClientIdState(clientId);
      } catch (error) {
        console.error('Error loading Google Calendar config:', error);
      }
    };

    loadGoogleCalendarConfig();
  }, [user?.id]);

  // Handle OAuth callback for Google Calendar
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      if (code && state === 'google_calendar' && user?.id) {
        setExpandedIntegrations(prev => ({ ...prev, 'google-calendar': true }));
        setGoogleCalendarLoading(true);
        setGoogleCalendarError(null);

        try {
          // Use Cloud Function to exchange code for tokens
          const functions = getFunctions();
          const exchangeCode = httpsCallable(functions, 'exchangeGoogleCode');

          await exchangeCode({
            code,
            redirectUri: window.location.origin,
          });

          // Reload config
          const config = await getUserGoogleCalendarConfig(user.id);
          setGoogleCalendarConfig(config);

          if (config?.connected) {
            const calendars = await listCalendars(user.id);
            setGoogleCalendars(calendars);
          }

          // Clear URL parameters
          window.history.replaceState({}, document.title, '/settings/integrations');
        } catch (error) {
          console.error('Error connecting Google Calendar:', error);
          setGoogleCalendarError(error instanceof Error ? error.message : 'Failed to connect Google Calendar');
        } finally {
          setGoogleCalendarLoading(false);
        }
      }
    };

    handleOAuthCallback();

    // QuickBooks OAuth callback
    const qbParams = new URLSearchParams(window.location.search);
    const qbCode = qbParams.get('code');
    const qbState = qbParams.get('state');
    const qbRealmId = qbParams.get('realmId');

    if (qbCode && qbState === 'quickbooks_oauth') {
      // Clear URL immediately to prevent double-execution in StrictMode
      window.history.replaceState({}, document.title, '/settings/integrations');
      setExpandedIntegrations(prev => ({ ...prev, quickbooks: true }));

      setQbConnecting(true);
      setQbConnectError(null);
      (async () => {
        try {
          const response = await crmAuthedFetch('https://us-central1-lctf-projects.cloudfunctions.net/exchangeQuickBooksCode', {
            method: 'POST',
            body: JSON.stringify({
              code: qbCode,
              redirectUri: 'https://clients.lctimberframes.com',
              realmId: qbRealmId,
            }),
          });
          const data = await response.json();
          if (data.success) {
            setQuickbooksSettings(prev => ({ ...prev, connected: true, realmId: qbRealmId || '' }));
          } else {
            // If already connected (token was exchanged on first run), don't show error
            if (qbRealmId) {
              setQuickbooksSettings(prev => ({ ...prev, connected: true, realmId: qbRealmId }));
            } else {
              setQbConnectError(data.error || 'Failed to connect');
            }
          }
        } catch (err) {
          setQbConnectError(err instanceof Error ? err.message : 'Connection failed');
        } finally {
          setQbConnecting(false);
        }
      })();
    }
  }, [user?.id]);

  // Google Calendar handlers
  const handleConnectGoogleCalendar = useCallback(async () => {
    if (!googleClientId) {
      setGoogleCalendarError('Please configure the Google Calendar Client ID first');
      return;
    }

    setGoogleCalendarLoading(true);
    setGoogleCalendarError(null);

    try {
      const redirectUri = window.location.origin;
      const authUrl = await getGoogleAuthUrl(redirectUri);
      // Add state parameter to identify the OAuth callback
      const urlWithState = authUrl + '&state=google_calendar';
      window.location.href = urlWithState;
    } catch (error) {
      console.error('Error initiating Google Calendar connection:', error);
      setGoogleCalendarError(error instanceof Error ? error.message : 'Failed to initiate connection');
      setGoogleCalendarLoading(false);
    }
  }, [googleClientId]);

  const handleDisconnectGoogleCalendar = useCallback(async () => {
    if (!user?.id) return;

    if (!window.confirm('Are you sure you want to disconnect Google Calendar?')) return;

    setGoogleCalendarLoading(true);

    try {
      await disconnectGoogleCalendar(user.id);
      setGoogleCalendarConfig(null);
      setGoogleCalendars([]);
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      setGoogleCalendarError(error instanceof Error ? error.message : 'Failed to disconnect');
    } finally {
      setGoogleCalendarLoading(false);
    }
  }, [user?.id]);

  const handleCalendarSelectionChange = useCallback(async (calendarId: string, selected: boolean) => {
    if (!user?.id || !googleCalendarConfig) return;

    const currentSelection = googleCalendarConfig.selectedCalendars || [];
    const newSelection = selected
      ? [...currentSelection, calendarId]
      : currentSelection.filter(id => id !== calendarId);

    try {
      await updateSelectedCalendars(user.id, newSelection);
      setGoogleCalendarConfig(prev => prev ? { ...prev, selectedCalendars: newSelection } : null);
    } catch (error) {
      console.error('Error updating calendar selection:', error);
    }
  }, [user?.id, googleCalendarConfig]);

  const handleSaveGoogleClientId = useCallback(async () => {
    setSavingGoogleClientId(true);
    try {
      await setGoogleClientId(googleClientId);
    } catch (error) {
      console.error('Error saving Google Client ID:', error);
    } finally {
      setSavingGoogleClientId(false);
    }
  }, [googleClientId]);

  const { crmUsers, pipelines } = useCRM();

  // Source colors state
  const [sourceColors, setSourceColorsState] = useState<Record<LeadSource, string>>(() => getSourceColors());

  // Update colors when localStorage changes (for real-time updates)
  useEffect(() => {
    const handleStorageChange = () => {
      setSourceColorsState(getSourceColors());
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleSourceColorChange = (source: LeadSource, color: string) => {
    setSourceColor(source, color);
    setSourceColorsState(getSourceColors());
    // Force re-render of map markers
    window.dispatchEvent(new Event('sourceColorsUpdated'));
  };

  const handleResetSourceColor = (source: LeadSource) => {
    resetSourceColor(source);
    setSourceColorsState(getSourceColors());
    window.dispatchEvent(new Event('sourceColorsUpdated'));
  };

  const handleResetAllColors = () => {
    if (window.confirm('Reset all source colors to defaults?')) {
      resetSourceColors();
      setSourceColorsState(getSourceColors());
      window.dispatchEvent(new Event('sourceColorsUpdated'));
    }
  };

  const allSources: LeadSource[] = ['unspecified', 'web-form', 'facebook-form', 'instagram-form', 'ebook', 'instagram-follow', 'facebook-follow', 'b2b-visit', 'cold-call', 'call-in', 'referral'];

  return (
    <div className="settings-section-page">
            

            

            

            

            {section === 'integrations' && (
              <div className="settings-section">
                <h3>Integrations</h3>
                <p className="setting-description">Connect third-party services used across the CRM.</p>
                <div className="settings-integration-list">
                  <div className="settings-integration-card" key="sendblue">
                    <button type="button" className="settings-integration-card-header" onClick={() => toggleIntegration('sendblue')} aria-expanded={!!expandedIntegrations['sendblue']}>
                      <div className="settings-integration-card-title">
                        <strong>Sendblue</strong>
                        {(() => {
                    const label = sendblueSettings.apiKey && sendblueSettings.apiSecret ? 'Configured' : 'Not configured';
                    const ok = label !== 'Not configured';
                    return (
                      <span className={`settings-status-chip ${ok ? 'settings-status-chip--ok' : 'settings-status-chip--muted'}`}>{label}</span>
                    );
                  })()}
                      </div>
                      {expandedIntegrations['sendblue'] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    {expandedIntegrations['sendblue'] && (
                      <div className="settings-integration-card-body">
                        <p className="setting-description">
                  Configure your Sendblue API credentials to enable SMS messaging with leads.
                  You can find your API credentials in your <a href="https://sendblue.co/dashboard" target="_blank" rel="noopener noreferrer">Sendblue dashboard</a>.
                </p>
                <div className="setting-item">
                  <label>API Key (sb-api-key-id)</label>
                  <input
                    type="password"
                    placeholder="Enter Sendblue API key"
                    value={sendblueSettings.apiKey}
                    onChange={(e) => setSendblueSettings(prev => ({ ...prev, apiKey: e.target.value }))}
                  />
                </div>
                <div className="setting-item">
                  <label>API Secret (sb-api-secret-key)</label>
                  <input
                    type="password"
                    placeholder="Enter Sendblue API secret"
                    value={sendblueSettings.apiSecret}
                    onChange={(e) => setSendblueSettings(prev => ({ ...prev, apiSecret: e.target.value }))}
                  />
                </div>
                <div className="setting-item" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="action-button primary"
                    onClick={async () => {
                      if (!sendblueSettings.apiKey || !sendblueSettings.apiSecret) return;

                      setSendblueSaving(true);
                      setSendblueSaveStatus('idle');
                      try {
                        await saveSendblueApiKey(sendblueSettings.apiKey);
                        await saveSendblueApiSecret(sendblueSettings.apiSecret);
                        setSendblueSaveStatus('saved');
                        setTimeout(() => setSendblueSaveStatus('idle'), 3000);
                      } catch (error) {
                        console.error('Error saving Sendblue credentials:', error);
                        setSendblueSaveStatus('error');
                      } finally {
                        setSendblueSaving(false);
                      }
                    }}
                    disabled={sendblueSaving || !sendblueSettings.apiKey || !sendblueSettings.apiSecret}
                  >
                    {sendblueSaving ? 'Saving...' : 'Save Configuration'}
                  </button>
                  {sendblueSaveStatus === 'saved' && (
                    <span style={{ color: 'var(--color-success-text)', fontSize: '14px' }}>Credentials saved successfully!</span>
                  )}
                  {sendblueSaveStatus === 'error' && (
                    <span style={{ color: 'var(--color-error-text)', fontSize: '14px' }}>Error saving credentials</span>
                  )}
                  {(!sendblueSettings.apiKey || !sendblueSettings.apiSecret) && sendblueSaveStatus === 'idle' && (
                    <span style={{ color: 'var(--color-text-muted)', fontSize: '13px' }}>Enter both API Key and Secret to save</span>
                  )}
                </div>
                {sendblueSettings.apiKey && sendblueSettings.apiSecret && (
                  <>
                    <div className="setting-divider"></div>
                    <h4>Test Your Configuration</h4>
                    <p className="setting-description">
                      Send a test message to verify your Sendblue credentials are working.
                    </p>
                    <div className="setting-item">
                      <label>Your Phone Number (to receive test)</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="tel"
                          placeholder="+1 (555) 123-4567"
                          value={sendblueTestNumber}
                          onChange={(e) => setSendblueTestNumber(e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className="action-button primary"
                          onClick={async () => {
                            if (!sendblueTestNumber.trim()) {
                              setSendblueTestResult({ success: false, message: 'Please enter a phone number' });
                              return;
                            }
                            setSendblueTesting(true);
                            setSendblueTestResult(null);
                            try {
                              const result = await sendSMS(
                                sendblueTestNumber,
                                `Test message from LCTF CRM - ${new Date().toLocaleString()}`
                              );
                              if (result.success) {
                                setSendblueTestResult({ success: true, message: 'Test message sent! Check your phone.' });
                              } else {
                                setSendblueTestResult({ success: false, message: result.error || 'Failed to send' });
                              }
                              console.log('Sendblue test result:', result);
                            } catch (error) {
                              setSendblueTestResult({ success: false, message: error instanceof Error ? error.message : 'Test failed' });
                            } finally {
                              setSendblueTesting(false);
                            }
                          }}
                          disabled={sendblueTesting || !sendblueTestNumber.trim()}
                        >
                          {sendblueTesting ? 'Sending...' : 'Send Test'}
                        </button>
                      </div>
                      {sendblueTestResult && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          background: sendblueTestResult.success ? '#f0fdf4' : '#fef2f2',
                          color: sendblueTestResult.success ? '#22c55e' : '#ef4444',
                          fontSize: '14px'
                        }}>
                          {sendblueTestResult.success ? '✓ ' : '✗ '}{sendblueTestResult.message}
                        </div>
                      )}
                    </div>
                    <div className="setting-item" style={{ marginTop: '16px', padding: '12px', background: 'var(--color-primary-subtle)', borderRadius: '8px' }}>
                      <span style={{ color: 'var(--color-success-text)' }}>✓ Sendblue is configured</span>
                      <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                        SMS/iMessage sending is enabled. Enter a phone number above to test.
                      </p>
                    </div>
                  </>
                )}
                      </div>
                    )}
                  </div>
                  <div className="settings-integration-card" key="sequence">
                    <button type="button" className="settings-integration-card-header" onClick={() => toggleIntegration('sequence')} aria-expanded={!!expandedIntegrations['sequence']}>
                      <div className="settings-integration-card-title">
                        <strong>Sequence</strong>
                        {(() => {
                    const autoRenew = !!sequenceSettings.refreshToken.trim();
                    const configured = autoRenew || !!sequenceSettings.accessToken.trim();
                    const label = !configured
                      ? 'Not configured'
                      : sequenceSettings.enabled
                        ? autoRenew
                          ? 'Auto-renew enabled'
                          : 'Enabled'
                        : autoRenew
                          ? 'Auto-renew ready'
                          : 'Configured';
                    return (
                      <span className={`settings-status-chip ${configured ? 'settings-status-chip--ok' : 'settings-status-chip--muted'}`}>{label}</span>
                    );
                  })()}
                      </div>
                      {expandedIntegrations['sequence'] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    {expandedIntegrations['sequence'] && (
                      <div className="settings-integration-card-body">
                        <p className="setting-description">
                  Use two credentials: a <strong>Platform API key</strong> (<code>sk_…</code>) for transaction sync, and a <strong>GraphQL session token</strong> to pull Card ID and last 4 digits for matching users to physical cards.
                </p>
                <div className="setting-description" style={{ fontSize: '14px', marginBottom: '12px', padding: '12px', background: 'var(--color-surface)', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
                  <strong>1. Platform API key</strong> (scheduled sync + transactions)
                  <ol style={{ margin: '8px 0 0 1.25rem', padding: 0 }}>
                    <li>Sequence → Settings → <strong>API Keys</strong> → copy key (<code>sk_…</code>)</li>
                    <li>Enable <strong>Read accounts</strong> and <strong>Read transfers</strong></li>
                  </ol>
                  <strong style={{ display: 'block', marginTop: '12px' }}>2. Card identity token</strong> (Card ID + last 4 — required for user matching)
                  <ol style={{ margin: '8px 0 0 1.25rem', padding: 0 }}>
                    <li>Log into <a href="https://app.getsequence.io" target="_blank" rel="noreferrer">app.getsequence.io</a></li>
                    <li>DevTools → <strong>Application</strong> → <strong>Session Storage</strong> → <code>https://app.getsequence.io</code></li>
                    <li>Copy <code>access_token</code> (starts with <code>eyJ</code>) — expires ~24h</li>
                    <li>Or add a refresh token below for auto-renewal (Network → filter <code>token</code> → POST to auth.getsequence.io on login)</li>
                  </ol>
                </div>
                <div className="setting-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={sequenceSettings.enabled}
                      onChange={(e) => setSequenceSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                    {' '}Enable scheduled weekday sync (8 AM &amp; 2 PM Central)
                  </label>
                </div>
                <div className="setting-item">
                  <label>Platform API key (<code>sk_…</code>)</label>
                  <input
                    type="password"
                    placeholder="sk_1fe61… (from Sequence → Settings → API Keys)"
                    value={sequenceSettings.accessToken}
                    onChange={(e) => setSequenceSettings(prev => ({ ...prev, accessToken: e.target.value }))}
                  />
                </div>
                <div className="setting-item">
                  <label>GraphQL session token (<code>eyJ…</code> from Session Storage)</label>
                  <input
                    type="password"
                    placeholder="eyJ… (access_token — Card ID + last 4 import)"
                    value={sequenceSettings.graphqlAccessToken}
                    onChange={(e) => setSequenceSettings(prev => ({ ...prev, graphqlAccessToken: e.target.value }))}
                  />
                </div>
                <details className="setting-item" style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                  <summary style={{ cursor: 'pointer' }}>Advanced: refresh token for auto-renewal</summary>
                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input
                      type="password"
                      placeholder="Refresh token (v1.…)"
                      value={sequenceSettings.refreshToken}
                      onChange={(e) => setSequenceSettings(prev => ({ ...prev, refreshToken: e.target.value }))}
                    />
                  </div>
                </details>
                {sequenceSettings.organizationId && (
                  <div className="setting-item">
                    <label>Organization ID</label>
                    <input type="text" value={sequenceSettings.organizationId} readOnly />
                  </div>
                )}
                <div className="setting-item" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="settings-save-btn"
                    onClick={async () => {
                      const hasAutoRenew = sequenceSettings.refreshToken.trim();
                      const hasBootstrap = sequenceSettings.accessToken.trim();
                      if (!hasAutoRenew && !hasBootstrap) return;
                      setSequenceSaving(true);
                      setSequenceSaveStatus('idle');
                      try {
                        await saveSequenceSettings({
                          enabled: sequenceSettings.enabled,
                          refreshToken: sequenceSettings.refreshToken,
                          auth0ClientId: sequenceSettings.auth0ClientId,
                          accessToken: sequenceSettings.accessToken,
                          graphqlAccessToken: sequenceSettings.graphqlAccessToken,
                          organizationId: sequenceSettings.organizationId,
                        });
                        setSequenceSaveStatus('saved');
                        setTimeout(() => setSequenceSaveStatus('idle'), 3000);
                      } catch (error) {
                        console.error('Error saving Sequence settings:', error);
                        setSequenceSaveStatus('error');
                      } finally {
                        setSequenceSaving(false);
                      }
                    }}
                    disabled={
                      sequenceSaving ||
                      !(sequenceSettings.refreshToken.trim() || sequenceSettings.accessToken.trim())
                    }
                  >
                    {sequenceSaving ? 'Saving...' : 'Save Configuration'}
                  </button>
                  {sequenceSaveStatus === 'saved' && (
                    <span style={{ color: 'var(--color-success-text)', fontSize: '14px' }}>Settings saved!</span>
                  )}
                  {sequenceSaveStatus === 'error' && (
                    <span style={{ color: 'var(--color-error-text)', fontSize: '14px' }}>Error saving settings</span>
                  )}
                </div>
                {(sequenceSettings.refreshToken || sequenceSettings.accessToken) && (
                  <>
                    <div className="setting-divider"></div>
                    <h4>Connection &amp; sync</h4>
                    <div className="setting-item" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="settings-save-btn"
                        disabled={sequenceTesting}
                        onClick={async () => {
                          setSequenceTesting(true);
                          setSequenceTestResult(null);
                          try {
                            await saveSequenceSettings({
                              enabled: sequenceSettings.enabled,
                              refreshToken: sequenceSettings.refreshToken,
                              auth0ClientId: sequenceSettings.auth0ClientId,
                              accessToken: sequenceSettings.accessToken,
                              graphqlAccessToken: sequenceSettings.graphqlAccessToken,
                              organizationId: sequenceSettings.organizationId,
                            });
                            const result = await testSequenceConnection({
                              refreshToken: sequenceSettings.refreshToken,
                              auth0ClientId: sequenceSettings.auth0ClientId,
                              accessToken: sequenceSettings.accessToken,
                              graphqlAccessToken: sequenceSettings.graphqlAccessToken,
                            });
                            setSequenceSettings(prev => ({ ...prev, organizationId: result.organizationId }));
                            const modeLabel =
                              result.authMode === 'platform-api'
                                ? 'Platform API'
                                : result.authMode === 'auth0-refresh'
                                  ? 'auto-renewing Auth0'
                                  : result.authMode === 'remote-api'
                                    ? 'Remote API + GraphQL'
                                    : 'web session / GraphQL';
                            const identityNote =
                              'identifiedCount' in result && typeof result.identifiedCount === 'number'
                                ? ` ${result.identifiedCount}/${result.cardCount} cards with Card ID + last 4.`
                                : '';
                            const gqlNote =
                              'graphqlConfigured' in result && result.graphqlConfigured === false
                                ? ' Add GraphQL session token for Card ID import.'
                                : '';
                            setSequenceTestResult({
                              success: true,
                              message: `Connected via ${modeLabel} — ${result.organizationName || result.organizationId}, ${result.cardCount} card(s), ${result.podCount} pod(s).${identityNote}${gqlNote}`,
                            });
                          } catch (error) {
                            setSequenceTestResult({
                              success: false,
                              message: error instanceof Error ? error.message : 'Connection test failed',
                            });
                          } finally {
                            setSequenceTesting(false);
                          }
                        }}
                      >
                        {sequenceTesting ? 'Testing…' : 'Test connection'}
                      </button>
                      <button
                        type="button"
                        className="settings-save-btn"
                        disabled={!!sequenceSyncing}
                        onClick={async () => {
                          setSequenceSyncing('cards');
                          setSequenceTestResult(null);
                          try {
                            await saveSequenceSettings({
                              enabled: sequenceSettings.enabled,
                              refreshToken: sequenceSettings.refreshToken,
                              auth0ClientId: sequenceSettings.auth0ClientId,
                              accessToken: sequenceSettings.accessToken,
                              graphqlAccessToken: sequenceSettings.graphqlAccessToken,
                              organizationId: sequenceSettings.organizationId,
                            });
                            const result = await syncSequenceCards({
                              refreshToken: sequenceSettings.refreshToken,
                              auth0ClientId: sequenceSettings.auth0ClientId,
                              accessToken: sequenceSettings.accessToken,
                              graphqlAccessToken: sequenceSettings.graphqlAccessToken,
                            });
                            const idPart =
                              result.identifiedCount != null
                                ? ` — ${result.identifiedCount} identified (Card ID + last 4)`
                                : '';
                            const txPart =
                              typeof result.transactionsWritten === 'number'
                                ? ` · ${result.transactionsWritten} transaction(s) cached, ${result.matched ?? 0} matched to QB${result.cardExpensesWritten || result.cardExpensesUpdated ? `, ${(result.cardExpensesWritten ?? 0) + (result.cardExpensesUpdated ?? 0)} added to expense list` : ''}`
                                : '';
                            setSequenceTestResult({
                              success: true,
                              message: `Synced ${result.imported} card(s) — ${result.linkedCount} linked in registry${idPart}${txPart}.`,
                            });
                            setSequenceSyncMeta(await getSequenceSyncMeta());
                          } catch (error) {
                            setSequenceTestResult({
                              success: false,
                              message: error instanceof Error ? error.message : 'Card sync failed',
                            });
                          } finally {
                            setSequenceSyncing(null);
                          }
                        }}
                      >
                        {sequenceSyncing === 'cards' ? 'Syncing cards…' : 'Sync cards from Sequence'}
                      </button>
                      <button
                        type="button"
                        className="settings-save-btn"
                        disabled={!!sequenceSyncing}
                        onClick={async () => {
                          setSequenceSyncing('transactions');
                          setSequenceTestResult(null);
                          try {
                            await saveSequenceSettings({
                              enabled: sequenceSettings.enabled,
                              refreshToken: sequenceSettings.refreshToken,
                              auth0ClientId: sequenceSettings.auth0ClientId,
                              accessToken: sequenceSettings.accessToken,
                              graphqlAccessToken: sequenceSettings.graphqlAccessToken,
                              organizationId: sequenceSettings.organizationId,
                            });
                            const result = await syncSequenceTransactions({
                              refreshToken: sequenceSettings.refreshToken,
                              auth0ClientId: sequenceSettings.auth0ClientId,
                              accessToken: sequenceSettings.accessToken,
                              graphqlAccessToken: sequenceSettings.graphqlAccessToken,
                            });
                            setSequenceTestResult({
                              success: true,
                              message: `Synced ${result.written} transaction(s) — matched ${result.matched}, assigned ${result.assigned}.${result.cardExpensesWritten || result.cardExpensesUpdated ? ` ${(result.cardExpensesWritten ?? 0) + (result.cardExpensesUpdated ?? 0)} added to expense list.` : ''}${result.shortTermOpsWritten || result.shortTermOpsStamped ? ` Short Term Ops: ${result.shortTermOpsWritten || 0} created, ${result.shortTermOpsStamped || 0} stamped.` : ''}`,
                            });
                            setSequenceSyncMeta(await getSequenceSyncMeta());
                          } catch (error) {
                            setSequenceTestResult({
                              success: false,
                              message: error instanceof Error ? error.message : 'Transaction sync failed',
                            });
                          } finally {
                            setSequenceSyncing(null);
                          }
                        }}
                      >
                        {sequenceSyncing === 'transactions' ? 'Syncing…' : 'Sync transactions now'}
                      </button>
                    </div>
                    {sequenceTestResult && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        background: sequenceTestResult.success ? '#f0fdf4' : '#fef2f2',
                        color: sequenceTestResult.success ? '#22c55e' : '#ef4444',
                        fontSize: '14px',
                      }}>
                        {sequenceTestResult.success ? '✓ ' : '✗ '}{sequenceTestResult.message}
                      </div>
                    )}
                    {(sequenceSyncMeta.lastCardSyncAt || sequenceSyncMeta.lastTransactionSyncAt) && (
                      <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginTop: '12px' }}>
                        {sequenceSyncMeta.lastCardSyncAt && (
                          <>Last card sync: {sequenceSyncMeta.lastCardSyncAt.toLocaleString()}{sequenceSyncMeta.lastCardCount != null ? ` (${sequenceSyncMeta.lastCardCount} cards)` : ''}. </>
                        )}
                        {sequenceSyncMeta.lastTransactionSyncAt && (
                          <>Last transaction sync: {sequenceSyncMeta.lastTransactionSyncAt.toLocaleString()}{sequenceSyncMeta.lastMatchCount != null ? ` — ${sequenceSyncMeta.lastMatchCount} matched` : ''}.</>
                        )}
                      </p>
                    )}
                  </>
                )}
                {user?.role === 'admin' && (
                  <>
                    <div className="setting-divider" />
                    <h4 id="sequence-sto">Short Term Ops external account</h4>
                    <p className="setting-description">
                      Sequence external-account charges (Short Term Ops) are not tied to a debit card. Assign them to one person so they appear in{' '}
                      <strong>My Card Expenses</strong> for categorization instead of only the admin team inbox.
                    </p>
                    <div className="setting-item">
                      <label htmlFor="short-term-ops-assignee">Assign external-account charges to</label>
                      <select
                        id="short-term-ops-assignee"
                        value={shortTermOpsAssigneeUserId}
                        onChange={e => setShortTermOpsAssigneeUserId(e.target.value)}
                      >
                        <option value="">— Not assigned —</option>
                        {crmUsers.filter(u => u.isActive).map(u => (
                          <option key={u.uid} value={u.uid}>
                            {u.displayName || u.email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="setting-item" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="settings-save-btn"
                        disabled={shortTermOpsAssigneeSaving || shortTermOpsAssigneeBackfilling}
                        onClick={async () => {
                          setShortTermOpsAssigneeSaving(true);
                          setShortTermOpsAssigneeStatus(null);
                          try {
                            const chosen = crmUsers.find(u => u.uid === shortTermOpsAssigneeUserId);
                            await saveShortTermOpsAssignee({
                              userId: shortTermOpsAssigneeUserId,
                              userName: chosen?.displayName || chosen?.email || '',
                              updatedBy: user?.id,
                            });
                            setShortTermOpsAssigneeStatus('Saved. Apply to pending charges to reassign existing rows.');
                          } catch (err) {
                            setShortTermOpsAssigneeStatus(
                              err instanceof Error ? err.message : 'Could not save Short Term Ops assignee.',
                            );
                          } finally {
                            setShortTermOpsAssigneeSaving(false);
                          }
                        }}
                      >
                        {shortTermOpsAssigneeSaving ? 'Saving…' : 'Save assignee'}
                      </button>
                      <button
                        type="button"
                        className="settings-save-btn"
                        disabled={
                          !shortTermOpsAssigneeUserId ||
                          shortTermOpsAssigneeSaving ||
                          shortTermOpsAssigneeBackfilling
                        }
                        onClick={async () => {
                          setShortTermOpsAssigneeBackfilling(true);
                          setShortTermOpsAssigneeStatus(null);
                          try {
                            const result = await backfillShortTermOpsAssignee();
                            setShortTermOpsAssigneeStatus(
                              `Updated ${result.updated} pending Short Term Ops charge(s). Skipped ${result.skipped}.`,
                            );
                          } catch (err) {
                            setShortTermOpsAssigneeStatus(
                              err instanceof Error ? err.message : 'Backfill failed.',
                            );
                          } finally {
                            setShortTermOpsAssigneeBackfilling(false);
                          }
                        }}
                      >
                        {shortTermOpsAssigneeBackfilling ? 'Applying…' : 'Apply to pending charges'}
                      </button>
                    </div>
                    {shortTermOpsAssigneeStatus && (
                      <p className="setting-description" style={{ marginTop: '8px' }}>
                        {shortTermOpsAssigneeStatus}
                      </p>
                    )}
                    <div className="setting-divider" />
                    <h4 id="card-expense-digest">Card expense digest</h4>
                    <p className="setting-description">
                      Reminders for pending card / Short Term Ops charges. Default is weekly (Monday 7 AM Central).
                      Auto-assigned overhead and excluded transfers are left out of the digest.
                    </p>
                    <div className="setting-item">
                      <label htmlFor="card-expense-digest-frequency">Digest frequency</label>
                      <select
                        id="card-expense-digest-frequency"
                        value={cardExpenseDigestFrequency}
                        onChange={e =>
                          setCardExpenseDigestFrequency(e.target.value as CardExpenseDigestFrequency)
                        }
                      >
                        <option value="weekly">Weekly (Monday)</option>
                        <option value="daily">Daily (weekdays)</option>
                        <option value="off">Off</option>
                      </select>
                    </div>
                    <div className="setting-item">
                      <button
                        type="button"
                        className="settings-save-btn"
                        disabled={cardExpenseDigestSaving}
                        onClick={async () => {
                          setCardExpenseDigestSaving(true);
                          setCardExpenseDigestStatus(null);
                          try {
                            await saveCardExpenseDigestFrequency({
                              digestFrequency: cardExpenseDigestFrequency,
                              updatedBy: user?.id,
                            });
                            setCardExpenseDigestStatus('Saved digest frequency.');
                          } catch (err) {
                            setCardExpenseDigestStatus(
                              err instanceof Error ? err.message : 'Could not save digest frequency.',
                            );
                          } finally {
                            setCardExpenseDigestSaving(false);
                          }
                        }}
                      >
                        {cardExpenseDigestSaving ? 'Saving…' : 'Save digest setting'}
                      </button>
                    </div>
                    {cardExpenseDigestStatus && (
                      <p className="setting-description" style={{ marginTop: '8px' }}>
                        {cardExpenseDigestStatus}
                      </p>
                    )}
                    <div className="setting-divider" />
                    <h4 id="card-expense-auto-rules">Short Term Ops auto-assign rules</h4>
                    <p className="setting-description">
                      When charges sync in, matching vendors are auto-assigned to overhead or marked not
                      applicable / excluded. Each charge stores which rule applied; staff can still override
                      in My Card Expenses.
                    </p>
                    <div className="setting-item" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {cardExpenseAutoRules.map((rule, idx) => (
                        <label
                          key={rule.id}
                          style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px' }}
                        >
                          <input
                            type="checkbox"
                            checked={rule.enabled !== false}
                            onChange={e => {
                              const next = [...cardExpenseAutoRules];
                              next[idx] = { ...rule, enabled: e.target.checked };
                              setCardExpenseAutoRules(next);
                            }}
                          />
                          <span>
                            <strong>{rule.label}</strong>
                            <br />
                            <span style={{ color: 'var(--color-text-secondary)' }}>
                              Vendors: {rule.vendorContains.join(', ') || '—'}
                              {rule.descriptionContains?.length
                                ? ` · Description: ${rule.descriptionContains.join(', ')}`
                                : ''}
                              {' · '}
                              {rule.action}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <div className="setting-item" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="settings-save-btn"
                        disabled={cardExpenseAutoRulesSaving}
                        onClick={async () => {
                          setCardExpenseAutoRulesSaving(true);
                          setCardExpenseAutoRulesStatus(null);
                          try {
                            await saveCardExpenseAutoAssignRules({
                              rules: cardExpenseAutoRules,
                              updatedBy: user?.id,
                            });
                            setCardExpenseAutoRulesStatus('Saved auto-assign rules.');
                          } catch (err) {
                            setCardExpenseAutoRulesStatus(
                              err instanceof Error ? err.message : 'Could not save auto-assign rules.',
                            );
                          } finally {
                            setCardExpenseAutoRulesSaving(false);
                          }
                        }}
                      >
                        {cardExpenseAutoRulesSaving ? 'Saving…' : 'Save auto-assign rules'}
                      </button>
                      <button
                        type="button"
                        className="settings-save-btn"
                        disabled={cardExpenseAutoRulesSaving}
                        onClick={() => {
                          setCardExpenseAutoRules(DEFAULT_CARD_EXPENSE_AUTO_RULES);
                          setCardExpenseAutoRulesStatus('Restored defaults (save to persist).');
                        }}
                      >
                        Reset to defaults
                      </button>
                    </div>
                    {cardExpenseAutoRulesStatus && (
                      <p className="setting-description" style={{ marginTop: '8px' }}>
                        {cardExpenseAutoRulesStatus}
                      </p>
                    )}
                    <div className="setting-divider" />
                    <h4 id="sequence-cards">Link cards to users</h4>
                    <p className="setting-description">
                      After syncing cards from Sequence, assign each card to a CRM user. Cardholders can then categorize their own charges in My Card Expenses.
                    </p>
                    <DebitCardSettingsPanel embedded />
                  </>
                )}
                      </div>
                    )}
                  </div>
                  <div className="settings-integration-card" key="quickbooks">
                    <button type="button" className="settings-integration-card-header" onClick={() => toggleIntegration('quickbooks')} aria-expanded={!!expandedIntegrations['quickbooks']}>
                      <div className="settings-integration-card-title">
                        <strong>QuickBooks</strong>
                        {(() => {
                    const label = quickbooksSettings.connected ? 'Connected' : quickbooksSettings.clientId ? 'Configured' : 'Not configured';
                    const ok = label !== 'Not configured';
                    return (
                      <span className={`settings-status-chip ${ok ? 'settings-status-chip--ok' : 'settings-status-chip--muted'}`}>{label}</span>
                    );
                  })()}
                      </div>
                      {expandedIntegrations['quickbooks'] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    {expandedIntegrations['quickbooks'] && (
                      <div className="settings-integration-card-body">
                        <p className="setting-description">
                  Automatically log payments from QuickBooks multi-use payment links. When a customer pays, the payment appears in their message thread, creates an activity, and can notify your team.
                </p>

                {/* Connection Status */}
                <div className="setting-item" style={{
                  padding: '16px',
                  background: quickbooksSettings.connected ? '#f0fdf4' : '#f8fafc',
                  borderRadius: '8px',
                  border: `1px solid ${quickbooksSettings.connected ? '#bbf7d0' : '#e2e8f0'}`,
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <span style={{
                        color: quickbooksSettings.connected ? '#22c55e' : '#64748b',
                        fontWeight: 600
                      }}>
                        {quickbooksSettings.connected ? '✓ QuickBooks Connected' : '○ QuickBooks Not Connected'}
                      </span>
                      {quickbooksSettings.connected && quickbooksSettings.realmId && (
                        <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                          Company ID: {quickbooksSettings.realmId}
                        </p>
                      )}
                    </div>
                    <span style={{
                      fontSize: '12px',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: quickbooksSettings.enabled ? '#dcfce7' : '#fef2f2',
                      color: quickbooksSettings.enabled ? '#166534' : '#991b1b'
                    }}>
                      {quickbooksSettings.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>

                <div className="setting-divider"></div>

                {/* API Credentials */}
                <h4>API Credentials</h4>
                <p className="setting-description" style={{ marginBottom: '12px' }}>
                  Get these from your <a href="https://developer.intuit.com/app/developer/dashboard" target="_blank" rel="noopener noreferrer">Intuit Developer Dashboard</a>
                </p>

                <div className="setting-item">
                  <label>Client ID</label>
                  <input
                    type="text"
                    value={quickbooksSettings.clientId}
                    onChange={(e) => setQuickbooksSettings(prev => ({ ...prev, clientId: e.target.value }))}
                    placeholder="Enter QuickBooks Client ID"
                  />
                </div>

                <div className="setting-item">
                  <label>Client Secret</label>
                  <input
                    type="password"
                    value={quickbooksSettings.clientSecret}
                    onChange={(e) => setQuickbooksSettings(prev => ({ ...prev, clientSecret: e.target.value }))}
                    placeholder="Enter QuickBooks Client Secret"
                  />
                </div>

                <div className="setting-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={quickbooksSettings.enabled}
                      onChange={(e) => setQuickbooksSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                    Enable QuickBooks Integration
                  </label>
                </div>

                <div className="setting-item" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="action-button primary"
                    onClick={async () => {
                      if (!quickbooksSettings.clientId || !quickbooksSettings.clientSecret) {
                        alert('Please enter both Client ID and Client Secret');
                        return;
                      }

                      setQuickbooksSaving(true);
                      setQuickbooksSaveStatus('idle');
                      try {
                        await updateSetting('quickbooksClientId', quickbooksSettings.clientId);
                        await updateSetting('quickbooksClientSecret', quickbooksSettings.clientSecret);
                        await updateSetting('quickbooksEnabled', quickbooksSettings.enabled);
                        setQuickbooksSaveStatus('saved');
                        setTimeout(() => setQuickbooksSaveStatus('idle'), 3000);
                      } catch (error) {
                        console.error('Error saving QuickBooks credentials:', error);
                        setQuickbooksSaveStatus('error');
                      } finally {
                        setQuickbooksSaving(false);
                      }
                    }}
                    disabled={quickbooksSaving || !quickbooksSettings.clientId || !quickbooksSettings.clientSecret}
                  >
                    {quickbooksSaving ? 'Saving...' : 'Save Credentials'}
                  </button>
                  {quickbooksSaveStatus === 'saved' && (
                    <span style={{ color: 'var(--color-success-text)', fontSize: '14px' }}>✓ Saved successfully!</span>
                  )}
                  {quickbooksSaveStatus === 'error' && (
                    <span style={{ color: 'var(--color-error-text)', fontSize: '14px' }}>✗ Error saving</span>
                  )}
                </div>

                <div className="setting-divider"></div>

                {/* OAuth Connect */}
                <h4>Connect to QuickBooks</h4>
                <p className="setting-description">
                  After saving your credentials above, click below to authorize access to your QuickBooks account. This allows the CRM to pull invoices, payments, and customer data.
                </p>
                <div className="setting-item" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {quickbooksSettings.connected ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: 'var(--color-success-text)', fontWeight: 600, fontSize: '14px' }}>✓ Connected</span>
                      {quickbooksSettings.realmId && (
                        <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>(Company ID: {quickbooksSettings.realmId})</span>
                      )}
                      <button
                        className="action-button"
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                        onClick={() => {
                          if (!quickbooksSettings.clientId) {
                            setQbConnectError('Save your Client ID first');
                            return;
                          }
                          const redirectUri = 'https://clients.lctimberframes.com';
                          const scope = 'com.intuit.quickbooks.accounting';
                          const authUrl = `https://appcenter.intuit.com/connect/oauth2?client_id=${quickbooksSettings.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${scope}&state=quickbooks_oauth`;
                          window.location.href = authUrl;
                        }}
                      >
                        Reconnect
                      </button>
                    </div>
                  ) : (
                    <button
                      className="action-button primary"
                      disabled={!quickbooksSettings.clientId || !quickbooksSettings.clientSecret || qbConnecting}
                      onClick={() => {
                        if (!quickbooksSettings.clientId) {
                          setQbConnectError('Save your Client ID and Secret first');
                          return;
                        }
                        setQbConnectError(null);
                        const redirectUri = 'https://clients.lctimberframes.com';
                        const scope = 'com.intuit.quickbooks.accounting';
                        const authUrl = `https://appcenter.intuit.com/connect/oauth2?client_id=${quickbooksSettings.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${scope}&state=quickbooks_oauth`;
                        window.location.href = authUrl;
                      }}
                    >
                      {qbConnecting ? 'Connecting…' : 'Connect to QuickBooks'}
                    </button>
                  )}
                  {qbConnectError && (
                    <span style={{ color: 'var(--color-error-text)', fontSize: '13px' }}>{qbConnectError}</span>
                  )}
                </div>

                <div className="setting-divider"></div>

                {/* Webhook URL */}
                <h4>Webhook URL</h4>
                <p className="setting-description">
                  Add this URL in your <a href="https://developer.intuit.com/app/developer/dashboard" target="_blank" rel="noopener noreferrer">Intuit Developer Dashboard</a> under Webhooks. Subscribe to <strong>Payment</strong> events.
                </p>
                <div className="setting-item">
                  <div className="api-code-block" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <code style={{ flex: 1, wordBreak: 'break-all' }}>
                      https://us-central1-lctf-projects.cloudfunctions.net/quickbooksPaymentWebhook
                    </code>
                    <button
                      className="copy-btn"
                      onClick={() => {
                        navigator.clipboard.writeText('https://us-central1-lctf-projects.cloudfunctions.net/quickbooksPaymentWebhook');
                        showCopyFeedback('Webhook URL copied');
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div className="setting-divider"></div>

                {/* Payment Automation Settings */}
                <h4>Payment Automation</h4>
                <p className="setting-description">
                  Configure what happens when a payment is received from QuickBooks.
                </p>

                <div className="setting-item">
                  <label>Notify Users on Payment</label>
                  <div className="user-checkboxes" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    marginTop: '8px',
                    padding: '12px',
                    background: 'var(--color-surface)',
                    borderRadius: '8px'
                  }}>
                    {crmUsers.filter(u => u.isActive).map(user => (
                      <label key={user.uid} className="checkbox-label" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={quickbooksSettings.paymentNotifyUsers.includes(user.uid)}
                          onChange={(e) => {
                            const newUsers = e.target.checked
                              ? [...quickbooksSettings.paymentNotifyUsers, user.uid]
                              : quickbooksSettings.paymentNotifyUsers.filter(id => id !== user.uid);
                            setQuickbooksSettings(prev => ({ ...prev, paymentNotifyUsers: newUsers }));
                          }}
                        />
                        <span>{user.displayName || user.email}</span>
                      </label>
                    ))}
                    {crmUsers.filter(u => u.isActive).length === 0 && (
                      <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>No active users found</span>
                    )}
                  </div>
                  <span className="setting-description">
                    These users will be notified and assigned a follow-up task when a payment is received.
                  </span>
                </div>

                <div className="setting-item">
                  <label>Move Deal to Stage on Payment</label>
                  <select
                    value={quickbooksSettings.paymentMoveToStage}
                    onChange={(e) => setQuickbooksSettings(prev => ({ ...prev, paymentMoveToStage: e.target.value }))}
                  >
                    <option value="">-- Don't move deal --</option>
                    {pipelines.length > 0 && pipelines[0].stages?.map(stage => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                  <span className="setting-description">
                    Automatically move the associated deal to this stage when a payment is received.
                  </span>
                </div>

                <div className="setting-divider"></div>

                {/* Payment Links Settings */}
                <h4>Payment Links</h4>
                <p className="setting-description">
                  Add multiple payment links with custom names. These can be quickly selected and sent from Messages or the Lead panel.
                </p>

                {/* List of existing payment links */}
                {quickbooksSettings.paymentLinks.length > 0 && (
                  <div className="payment-links-list" style={{ marginBottom: '16px' }}>
                    {quickbooksSettings.paymentLinks.map((link, index) => (
                      <div key={link.id} className="payment-link-item" style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        padding: '12px',
                        background: 'var(--color-surface)',
                        borderRadius: '8px',
                        marginBottom: '8px'
                      }}>
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            value={link.name}
                            onChange={(e) => {
                              const updated = [...quickbooksSettings.paymentLinks];
                              updated[index] = { ...updated[index], name: e.target.value };
                              setQuickbooksSettings(prev => ({ ...prev, paymentLinks: updated }));
                            }}
                            placeholder="Link name (e.g., Deposit, Final Payment)"
                            style={{ width: '100%', marginBottom: '6px' }}
                          />
                          <input
                            type="url"
                            value={link.url}
                            onChange={(e) => {
                              const updated = [...quickbooksSettings.paymentLinks];
                              updated[index] = { ...updated[index], url: e.target.value };
                              setQuickbooksSettings(prev => ({ ...prev, paymentLinks: updated }));
                            }}
                            placeholder="https://app.qbo.intuit.com/checkout/..."
                            style={{ width: '100%' }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = quickbooksSettings.paymentLinks.filter((_, i) => i !== index);
                            setQuickbooksSettings(prev => ({ ...prev, paymentLinks: updated }));
                          }}
                          style={{
                            background: 'var(--color-warning-light)',
                            color: 'var(--color-error-text)',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                          title="Remove link"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new link button */}
                <div className="setting-item">
                  <button
                    type="button"
                    onClick={() => {
                      const newLink = {
                        id: `link-${Date.now()}`,
                        name: '',
                        url: ''
                      };
                      setQuickbooksSettings(prev => ({
                        ...prev,
                        paymentLinks: [...prev.paymentLinks, newLink]
                      }));
                    }}
                    style={{
                      background: 'var(--color-primary-subtle)',
                      color: 'var(--color-success-text)',
                      border: '1px dashed #16a34a',
                      borderRadius: '8px',
                      padding: '10px 16px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      width: '100%'
                    }}
                  >
                    + Add Payment Link
                  </button>
                </div>

                <div className="setting-item" style={{ marginTop: '12px' }}>
                  <label>Move to Stage on Send</label>
                  <select
                    value={quickbooksSettings.paymentRequestedStage}
                    onChange={(e) => setQuickbooksSettings(prev => ({ ...prev, paymentRequestedStage: e.target.value }))}
                  >
                    <option value="">-- Don't move deal --</option>
                    {pipelines.length > 0 && pipelines[0].stages?.map(stage => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                  <span className="setting-description">
                    Automatically move the lead's deal to this stage when you send a payment link.
                  </span>
                </div>

                <div className="setting-divider"></div>

                <h4>Payment Link Assignments</h4>
                <p className="setting-description">
                  Assign specific payment links to engagement letter types.
                </p>

                <div className="setting-item">
                  <label>Concept Payment Link</label>
                  <select
                    value={conceptPaymentLinkId}
                    onChange={(e) => setConceptPaymentLinkId(e.target.value)}
                  >
                    <option value="">-- Select a Link --</option>
                    {quickbooksSettings.paymentLinks.map(link => (
                      <option key={link.id} value={link.id}>{link.name}</option>
                    ))}
                  </select>
                </div>

                <div className="setting-item">
                  <label>Retainer Payment Link</label>
                  <select
                    value={retainerPaymentLinkId}
                    onChange={(e) => setRetainerPaymentLinkId(e.target.value)}
                  >
                    <option value="">-- Select a Link --</option>
                    {quickbooksSettings.paymentLinks.map(link => (
                      <option key={link.id} value={link.id}>{link.name}</option>
                    ))}
                  </select>
                </div>

                <div className="setting-divider"></div>

                {/* Expense Category Classification */}
                <h4>Expense Categories (Project Economics)</h4>
                <p className="setting-description">
                  How each QuickBooks account is treated in Project Economics / P&amp;L reports: &quot;Direct Cost&quot; spend counts against the matched project, &quot;Overhead&quot; is allocated proportionally across projects. Unclassified accounts appear as unmatched. This is separate from card expense groups below, which control what cardholders pick on My Card Expenses.
                </p>
                {allExpenseAccounts.length === 0 ? (
                  <div style={{ padding: '16px', background: 'var(--color-surface)', borderRadius: '8px', color: 'var(--color-text-secondary)', fontSize: '13px', fontStyle: 'italic' }}>
                    No expense accounts found yet. Sync QuickBooks from the Sales Activity page to populate accounts.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '400px', overflowY: 'auto', padding: '8px 0' }}>
                    {allExpenseAccounts.map(account => {
                      const isDirect = expenseCategories.direct.includes(account);
                      const isOverhead = expenseCategories.overhead.includes(account);
                      const current = isDirect ? 'direct' : isOverhead ? 'overhead' : 'unmatched';
                      return (
                        <div key={account} style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '8px 12px', background: 'var(--color-surface)', borderRadius: '6px',
                          borderLeft: `3px solid ${current === 'direct' ? '#3B82F6' : current === 'overhead' ? '#F59E0B' : '#CBD5E1'}`,
                        }}>
                          <span style={{ flex: 1, fontSize: '13px', fontWeight: 500 }}>{account}</span>
                          <select
                            value={current}
                            onChange={(e) => {
                              const val = e.target.value;
                              setExpenseCategories(prev => ({
                                overhead: val === 'overhead'
                                  ? [...prev.overhead.filter(a => a !== account), account]
                                  : prev.overhead.filter(a => a !== account),
                                direct: val === 'direct'
                                  ? [...prev.direct.filter(a => a !== account), account]
                                  : prev.direct.filter(a => a !== account),
                              }));
                            }}
                            style={{
                              fontSize: '12px', padding: '4px 8px', borderRadius: '4px',
                              border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer',
                              color: current === 'direct' ? '#3B82F6' : current === 'overhead' ? '#D97706' : '#64748B',
                              fontWeight: 600,
                            }}
                          >
                            <option value="unmatched">Unclassified</option>
                            <option value="direct">Direct Cost</option>
                            <option value="overhead">Overhead</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                )}
                {allExpenseAccounts.length > 0 && (
                  <div style={{ marginTop: '8px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <button
                      className="action-button primary"
                      disabled={expenseCatSaving}
                      onClick={async () => {
                        setExpenseCatSaving(true);
                        try {
                          await setDoc(doc(db, 'settings', 'expenseCategories'), expenseCategories);
                        } catch (err) {
                          console.error('Error saving expense categories:', err);
                        } finally {
                          setExpenseCatSaving(false);
                        }
                      }}
                    >
                      {expenseCatSaving ? 'Saving...' : 'Save Categories'}
                    </button>
                    <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                      {expenseCategories.direct.length} direct &middot; {expenseCategories.overhead.length} overhead &middot; {allExpenseAccounts.length - expenseCategories.direct.length - expenseCategories.overhead.length} unclassified
                    </span>
                  </div>
                )}

                <div className="setting-divider"></div>

                <h4>Card expense groups</h4>
                <p className="setting-description">
                  Groups define what cardholders pick on My Card Expenses. Each group maps to one or more QuickBooks
                  accounts so categorization writes back automatically.
                </p>
                <div
                  style={{
                    padding: '14px 16px',
                    background: 'var(--color-surface)',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 4 }}>
                      {expenseLegendRows.length} groups · {countLegendMappedAccounts(expenseLegendRows)} QB accounts mapped
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      {expenseLegendUpdatedAt
                        ? `Last saved ${expenseLegendUpdatedAt.toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}`
                        : 'Not saved yet'}
                    </div>
                    {groupsMissingAccounts(expenseLegendRows).length > 0 && (
                      <span
                        style={{
                          display: 'inline-block',
                          marginTop: 8,
                          padding: '2px 8px',
                          fontSize: 11,
                          fontWeight: 600,
                          borderRadius: 999,
                          background: 'var(--color-warning-light)',
                          color: 'var(--color-warning-text)',
                        }}
                      >
                        {groupsMissingAccounts(expenseLegendRows).length} group
                        {groupsMissingAccounts(expenseLegendRows).length === 1 ? '' : 's'} missing QB accounts
                      </span>
                    )}
                  </div>
                  <button
                    ref={manageGroupsBtnRef}
                    type="button"
                    className="action-button primary"
                    onClick={() => setExpenseGroupsModalOpen(true)}
                  >
                    Manage groups…
                  </button>
                </div>

                {expenseGroupsModalOpen && (
                  <ExpenseGroupManagerModal
                    onClose={() => {
                      setExpenseGroupsModalOpen(false);
                      manageGroupsBtnRef.current?.focus();
                    }}
                    onSaved={savedRows => {
                      setExpenseLegendRows(savedRows);
                      setExpenseLegendUpdatedAt(new Date());
                    }}
                  />
                )}

                <div className="setting-divider"></div>

                {/* Invoice Creation */}
                <h4>Auto Invoice Creation</h4>
                <p className="setting-description" style={{ marginBottom: '12px' }}>
                  Automatically create QuickBooks invoices when a deal is signed. Map your proposal line item categories to existing QuickBooks products/services.
                </p>

                <div className="setting-item" style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                  <button
                    type="button"
                    className="action-button"
                    disabled={qbItemsSyncing || !quickbooksSettings.connected}
                    onClick={async () => {
                      setQbItemsSyncing(true);
                      try {
                        const resp = await crmAuthedFetch('https://us-central1-lctf-projects.cloudfunctions.net/syncQuickBooksItems', { method: 'POST' });
                        const text = await resp.text();
                        let data: any;
                        try { data = JSON.parse(text); } catch { data = { success: false, error: text.substring(0, 200) }; }
                        if (data.success) {
                          const itemsDoc = await getDoc(doc(db, 'settings', 'quickbooksItems'));
                          if (itemsDoc.exists()) setQbItems(itemsDoc.data().items || []);
                          alert(`Synced ${data.count} items from QuickBooks`);
                        } else {
                          alert(`Sync failed: ${data.error || `HTTP ${resp.status}`}`);
                        }
                      } catch (err) {
                        console.error('QB items sync error:', err);
                        alert('Failed to sync items. Check the console for details.');
                      } finally {
                        setQbItemsSyncing(false);
                      }
                    }}
                  >
                    {qbItemsSyncing ? 'Syncing...' : `Sync QB Items${qbItems.length ? ` (${qbItems.length})` : ''}`}
                  </button>
                  {!quickbooksSettings.connected && (
                    <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>Connect QuickBooks first</span>
                  )}
                </div>

                {qbItems.length > 0 && (
                  <>
                    <div className="setting-item">
                      <label>Design & Engineering → QB Item</label>
                      <select
                        value={qbInvoiceMapping.designItemId}
                        onChange={(e) => {
                          const item = qbItems.find(i => i.qbId === e.target.value);
                          setQbInvoiceMapping(prev => ({ ...prev, designItemId: e.target.value, designItemName: item?.name || '' }));
                        }}
                      >
                        <option value="">— Select QB Item —</option>
                        {qbItems.filter(i => i.active).map(i => (
                          <option key={i.qbId} value={i.qbId}>{i.name}{i.type !== 'Service' ? ` (${i.type})` : ''}</option>
                        ))}
                      </select>
                      <p className="setting-description" style={{ marginTop: 4, marginBottom: 0, fontSize: 11 }}>
                        When "Design included in kit" is enabled on a proposal, design cost is automatically rolled into the timber kit line item instead.
                      </p>
                    </div>

                    <div className="setting-item">
                      <label>Timber Kit (default) → QB Item</label>
                      <select
                        value={qbInvoiceMapping.timberKitItemId}
                        onChange={(e) => {
                          const item = qbItems.find(i => i.qbId === e.target.value);
                          setQbInvoiceMapping(prev => ({ ...prev, timberKitItemId: e.target.value, timberKitItemName: item?.name || '' }));
                        }}
                      >
                        <option value="">— Select QB Item —</option>
                        {qbItems.filter(i => i.active).map(i => (
                          <option key={i.qbId} value={i.qbId}>{i.name}{i.type !== 'Service' ? ` (${i.type})` : ''}</option>
                        ))}
                      </select>
                      <p className="setting-description" style={{ marginTop: 4, marginBottom: 0, fontSize: 11 }}>
                        Fallback when no species-specific mapping is found.
                      </p>
                    </div>

                    {/* Species-specific timber kit mappings */}
                    <div className="setting-item">
                      <label>Species → QB Item Mapping</label>
                      <p className="setting-description" style={{ marginTop: 0, marginBottom: 8, fontSize: 11 }}>
                        Map specific wood species from proposals to their matching QB product.
                      </p>
                      {Object.entries(qbInvoiceMapping.timberKitSpeciesMap || {}).map(([species, item]) => (
                        <div key={species} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ minWidth: 100, fontSize: 13, fontWeight: 500 }}>{species}</span>
                          <select
                            value={item.id}
                            onChange={(e) => {
                              const qbItem = qbItems.find(i => i.qbId === e.target.value);
                              setQbInvoiceMapping(prev => ({
                                ...prev,
                                timberKitSpeciesMap: {
                                  ...prev.timberKitSpeciesMap,
                                  [species]: { id: e.target.value, name: qbItem?.name || '' },
                                },
                              }));
                            }}
                            style={{ flex: 1 }}
                          >
                            <option value="">— Select QB Item —</option>
                            {qbItems.filter(i => i.active).map(i => (
                              <option key={i.qbId} value={i.qbId}>{i.name}{i.type !== 'Service' ? ` (${i.type})` : ''}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            style={{ background: 'none', border: 'none', color: 'var(--color-error-text)', cursor: 'pointer', fontSize: 16, padding: '2px 6px' }}
                            onClick={() => {
                              setQbInvoiceMapping(prev => {
                                const updated = { ...prev.timberKitSpeciesMap };
                                delete updated[species];
                                return { ...prev, timberKitSpeciesMap: updated };
                              });
                            }}
                            title="Remove mapping"
                          >×</button>
                        </div>
                      ))}
                      {(() => {
                        const unmapped = availableSpecies.filter(s => !qbInvoiceMapping.timberKitSpeciesMap?.[s]);
                        return unmapped.length > 0 ? (
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                            <select
                              value={newSpeciesName}
                              onChange={(e) => setNewSpeciesName(e.target.value)}
                              style={{ flex: 1 }}
                            >
                              <option value="">— Add species mapping —</option>
                              {unmapped.map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="action-button"
                              disabled={!newSpeciesName}
                              onClick={() => {
                                if (!newSpeciesName) return;
                                setQbInvoiceMapping(prev => ({
                                  ...prev,
                                  timberKitSpeciesMap: {
                                    ...prev.timberKitSpeciesMap,
                                    [newSpeciesName]: { id: '', name: '' },
                                  },
                                }));
                                setNewSpeciesName('');
                              }}
                            >
                              Add
                            </button>
                          </div>
                        ) : (
                          <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '4px 0 0' }}>All species mapped.</p>
                        );
                      })()}
                    </div>

                    <div className="setting-item">
                      <label>Installation → QB Item</label>
                      <select
                        value={qbInvoiceMapping.installItemId}
                        onChange={(e) => {
                          const item = qbItems.find(i => i.qbId === e.target.value);
                          setQbInvoiceMapping(prev => ({ ...prev, installItemId: e.target.value, installItemName: item?.name || '' }));
                        }}
                      >
                        <option value="">— Select QB Item —</option>
                        {qbItems.filter(i => i.active).map(i => (
                          <option key={i.qbId} value={i.qbId}>{i.name}{i.type !== 'Service' ? ` (${i.type})` : ''}</option>
                        ))}
                      </select>
                    </div>

                    <div className="setting-item">
                      <label>Payment Terms (days)</label>
                      <select
                        value={qbInvoiceMapping.paymentTermsDays}
                        onChange={(e) => setQbInvoiceMapping(prev => ({ ...prev, paymentTermsDays: parseInt(e.target.value) }))}
                      >
                        <option value={0}>Due on Receipt</option>
                        <option value={15}>Net 15</option>
                        <option value={30}>Net 30</option>
                        <option value={45}>Net 45</option>
                        <option value={60}>Net 60</option>
                      </select>
                    </div>

                    <div className="setting-item">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={qbInvoiceMapping.enabled}
                          onChange={(e) => setQbInvoiceMapping(prev => ({ ...prev, enabled: e.target.checked }))}
                        />
                        Enable QB Invoice Creation
                      </label>
                    </div>

                    <div className="setting-item">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={qbInvoiceMapping.autoCreateOnSign}
                          onChange={(e) => setQbInvoiceMapping(prev => ({ ...prev, autoCreateOnSign: e.target.checked }))}
                        />
                        Auto-create invoice when proposal is signed
                      </label>
                    </div>

                    <div className="setting-item">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={qbInvoiceMapping.autoSendEmail}
                          onChange={(e) => setQbInvoiceMapping(prev => ({ ...prev, autoSendEmail: e.target.checked }))}
                        />
                        Auto-email invoice to client
                      </label>
                    </div>

                    <div className="setting-item" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="action-button primary"
                        disabled={qbMappingSaving}
                        onClick={async () => {
                          setQbMappingSaving(true);
                          setQbMappingSaveStatus('idle');
                          try {
                            await setDoc(doc(db, 'settings', 'qbInvoiceMapping'), qbInvoiceMapping);
                            setQbMappingSaveStatus('saved');
                            setTimeout(() => setQbMappingSaveStatus('idle'), 3000);
                          } catch (err) {
                            console.error('Error saving QB invoice mapping:', err);
                            setQbMappingSaveStatus('error');
                          } finally {
                            setQbMappingSaving(false);
                          }
                        }}
                      >
                        {qbMappingSaving ? 'Saving...' : 'Save Invoice Settings'}
                      </button>
                      {qbMappingSaveStatus === 'saved' && (
                        <span style={{ color: 'var(--color-success-text)', fontSize: '14px' }}>Saved</span>
                      )}
                      {qbMappingSaveStatus === 'error' && (
                        <span style={{ color: 'var(--color-error-text)', fontSize: '14px' }}>Error saving</span>
                      )}
                    </div>
                  </>
                )}

                <div className="setting-divider"></div>

                {/* How It Works */}
                <details>
                  <summary><strong>How It Works</strong></summary>
                  <div style={{ marginTop: '12px', padding: '12px', background: 'var(--color-surface)', borderRadius: '8px' }}>
                    <ol style={{ marginLeft: '20px', lineHeight: '1.8' }}>
                      <li>Customer clicks your QuickBooks multi-use payment link</li>
                      <li>Customer completes payment in QuickBooks</li>
                      <li>QuickBooks sends webhook to your CRM</li>
                      <li>CRM matches payment to lead by name/email</li>
                      <li>Payment appears inline in message thread</li>
                      <li>Activity created in lead timeline</li>
                      <li>Selected users are notified with follow-up task</li>
                      <li>Deal moves to selected stage (if configured)</li>
                    </ol>
                  </div>
                </details>
                      </div>
                    )}
                  </div>
                  <div className="settings-integration-card" key="slack">
                    <button type="button" className="settings-integration-card-header" onClick={() => toggleIntegration('slack')} aria-expanded={!!expandedIntegrations['slack']}>
                      <div className="settings-integration-card-title">
                        <strong>Slack</strong>
                        {(() => {
                    const label = slackSettings.botToken && slackSettings.channelId ? 'Configured' : 'Not configured';
                    const ok = label !== 'Not configured';
                    return (
                      <span className={`settings-status-chip ${ok ? 'settings-status-chip--ok' : 'settings-status-chip--muted'}`}>{label}</span>
                    );
                  })()}
                      </div>
                      {expandedIntegrations['slack'] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    {expandedIntegrations['slack'] && (
                      <div className="settings-integration-card-body">
                        <p className="setting-description">
                  Post CRM comments to Slack: <strong>lead</strong> card comments, <strong>project task</strong> comments (Project Hub), and PDF annotations. @mentions are resolved to Slack user links when possible. The same channel is used for all types.
                </p>

                <div className="setting-item">
                  <label>Bot Token</label>
                  <input
                    type="password"
                    value={slackSettings.botToken}
                    onChange={(e) => setSlackSettings(prev => ({ ...prev, botToken: e.target.value }))}
                    onBlur={async () => {
                      setSlackSaving(true);
                      setSlackSaveStatus('idle');
                      try {
                        await saveSlackSettings(slackSettings.botToken, slackSettings.channelId);
                        setSlackSaveStatus('saved');
                      } catch {
                        setSlackSaveStatus('error');
                      } finally {
                        setSlackSaving(false);
                      }
                    }}
                    placeholder="xoxb-..."
                    disabled={slackSaving}
                  />
                  <span className="setting-description">
                    Create a Slack app at api.slack.com, add the <code>chat:write</code> scope, install to your workspace, and paste the Bot User OAuth Token here.
                  </span>
                </div>

                <div className="setting-item">
                  <label>Channel</label>
                  <input
                    type="text"
                    value={slackSettings.channelId}
                    onChange={(e) => setSlackSettings(prev => ({ ...prev, channelId: e.target.value }))}
                    onBlur={async () => {
                      setSlackSaving(true);
                      setSlackSaveStatus('idle');
                      try {
                        await saveSlackSettings(slackSettings.botToken, slackSettings.channelId);
                        setSlackSaveStatus('saved');
                      } catch {
                        setSlackSaveStatus('error');
                      } finally {
                        setSlackSaving(false);
                      }
                    }}
                    placeholder="#crm-leads or channel ID (e.g. C01234567)"
                    disabled={slackSaving}
                  />
                  <span className="setting-description">
                    The #CRM channel for milestone posts (signed, paid, RFQ, OPS review, new lead). Use channel name (e.g. <code>#crm</code>) or channel ID. The bot must be invited to the channel.
                  </span>
                </div>

                {slackSaveStatus === 'saved' && (
                  <p className="setting-description" style={{ color: 'var(--color-success-text)' }}>✓ Settings saved</p>
                )}
                {slackSaveStatus === 'error' && (
                  <p className="setting-description" style={{ color: 'var(--color-error-text)' }}>Failed to save</p>
                )}

                <div className="setting-divider"></div>

                <div className="setting-item">
                  <h4>Setup steps:</h4>
                  <ol style={{ marginLeft: '20px', marginTop: '8px' }}>
                    <li>Go to <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer">api.slack.com/apps</a></li>
                    <li>Create New App → From scratch</li>
                    <li>Add <code>chat:write</code> under OAuth &amp; Permissions → Scopes</li>
                    <li>Install app to your workspace</li>
                    <li>Copy the Bot User OAuth Token and paste above</li>
                    <li>Invite the bot to your channel: <code>/invite @YourAppName</code></li>
                  </ol>
                </div>
                      </div>
                    )}
                  </div>
                  <div className="settings-integration-card" key="google-calendar">
                    <button type="button" className="settings-integration-card-header" onClick={() => toggleIntegration('google-calendar')} aria-expanded={!!expandedIntegrations['google-calendar']}>
                      <div className="settings-integration-card-title">
                        <strong>Google Calendar</strong>
                        {(() => {
                    const label = googleCalendarConfig?.connected ? 'Connected' : 'Not connected';
                    const ok = label === 'Connected';
                    return (
                      <span className={`settings-status-chip ${ok ? 'settings-status-chip--ok' : 'settings-status-chip--muted'}`}>{label}</span>
                    );
                  })()}
                      </div>
                      {expandedIntegrations['google-calendar'] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    {expandedIntegrations['google-calendar'] && (
                      <div className="settings-integration-card-body">
                        <p className="setting-description">
                  Connect your Google Calendar to see your events in the CRM calendar and optionally sync CRM activities to Google Calendar.
                </p>

                {googleCalendarError && (
                  <div className="error-message" style={{
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'var(--color-error-light)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-error-text)'
                  }}>
                    {googleCalendarError}
                  </div>
                )}

                {/* Admin: Client ID Configuration */}
                <div className="setting-item">
                  <label>OAuth Client ID</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={googleClientId}
                      onChange={(e) => setGoogleClientIdState(e.target.value)}
                      placeholder="Enter Google OAuth Client ID"
                      style={{ flex: 1 }}
                    />
                    <button
                      className="action-button"
                      onClick={handleSaveGoogleClientId}
                      disabled={savingGoogleClientId}
                    >
                      {savingGoogleClientId ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  <p className="setting-description">
                    The OAuth Client ID from your Google Cloud Console.
                    <a href="/docs/GOOGLE_CALENDAR_SETUP.md" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '4px' }}>
                      See setup instructions
                    </a>
                  </p>
                </div>

                <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid var(--color-border)' }} />

                {/* Connection Status */}
                {googleCalendarConfig?.connected ? (
                  <>
                    <div className="setting-item" style={{
                      padding: '16px',
                      background: 'var(--color-primary-subtle)',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <span style={{ color: 'var(--color-success-text)', fontWeight: 600 }}>
                            ✓ Connected to Google Calendar
                          </span>
                          <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                            {googleCalendarConfig.email}
                          </p>
                        </div>
                        <button
                          className="action-button danger"
                          onClick={handleDisconnectGoogleCalendar}
                          disabled={googleCalendarLoading}
                        >
                          Disconnect
                        </button>
                      </div>
                    </div>

                    {/* Calendar Selection */}
                    <div className="setting-item">
                      <label>Calendars to Sync</label>
                      <p className="setting-description">
                        Select which calendars to show in the CRM and sync events to.
                      </p>
                      <div className="calendar-list" style={{
                        marginTop: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {googleCalendars.length === 0 ? (
                          <p style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                            Loading calendars...
                          </p>
                        ) : (
                          googleCalendars.map(calendar => (
                            <label
                              key={calendar.id}
                              className="calendar-item"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px',
                                background: 'var(--color-surface)',
                                borderRadius: '8px',
                                cursor: 'pointer',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={googleCalendarConfig.selectedCalendars?.includes(calendar.id) || false}
                                onChange={(e) => handleCalendarSelectionChange(calendar.id, e.target.checked)}
                              />
                              <span
                                className="calendar-color"
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  backgroundColor: calendar.backgroundColor || '#4285f4',
                                }}
                              />
                              <span style={{ flex: 1 }}>
                                {calendar.summary}
                                {calendar.primary && (
                                  <span style={{
                                    marginLeft: '8px',
                                    fontSize: '11px',
                                    color: 'var(--color-text-secondary)',
                                    background: 'var(--pm-surface-hover)',
                                    padding: '2px 6px',
                                    borderRadius: '4px'
                                  }}>
                                    Primary
                                  </span>
                                )}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    {googleCalendarConfig.lastSyncAt && (
                      <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '16px' }}>
                        Last synced: {new Date(googleCalendarConfig.lastSyncAt).toLocaleString()}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="setting-item">
                    <div style={{
                      padding: '24px',
                      textAlign: 'center',
                      background: 'var(--color-surface)',
                      borderRadius: '12px',
                      border: '2px dashed var(--color-border)'
                    }}>
                      <p style={{ marginBottom: '16px', color: 'var(--color-text-secondary)' }}>
                        Connect your Google Calendar to sync events
                      </p>
                      <button
                        className="action-button primary"
                        onClick={handleConnectGoogleCalendar}
                        disabled={googleCalendarLoading || !googleClientId}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 24px',
                        }}
                      >
                        {googleCalendarLoading ? (
                          'Connecting...'
                        ) : (
                          <>
                            <Calendar size={16} style={{ marginRight: 8 }} />
                            Connect Google Calendar
                          </>
                        )}
                      </button>
                      {!googleClientId && (
                        <p style={{ marginTop: '12px', fontSize: '13px', color: 'var(--color-error-text)' }}>
                          Please configure the OAuth Client ID above first.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid var(--color-border)' }} />

                {/* Booking Link */}
                <h4 style={{ marginBottom: '8px' }}>Booking Link</h4>
                <p className="setting-description" style={{ marginBottom: '16px' }}>
                  Add your scheduling/booking link (e.g., Calendly, Cal.com) to quickly send from Messages.
                </p>

                <div className="setting-item">
                  <label>Booking URL</label>
                  <input
                    type="url"
                    value={bookingLink}
                    onChange={(e) => setBookingLink(e.target.value)}
                    placeholder="https://calendly.com/your-link"
                  />
                  <span className="setting-description">
                    This link will appear as a quick action in the Messages panel.
                  </span>
                </div>
                      </div>
                    )}
                  </div>
                  <div className="settings-integration-card" key="ai-models">
                    <button type="button" className="settings-integration-card-header" onClick={() => toggleIntegration('ai-models')} aria-expanded={!!expandedIntegrations['ai-models']}>
                      <div className="settings-integration-card-title">
                        <strong>AI Models</strong>
                        {(() => {
                    const label = geminiApiKey || openaiApiKey || anthropicApiKey ? 'Configured' : 'Not configured';
                    const ok = label !== 'Not configured';
                    return (
                      <span className={`settings-status-chip ${ok ? 'settings-status-chip--ok' : 'settings-status-chip--muted'}`}>{label}</span>
                    );
                  })()}
                      </div>
                      {expandedIntegrations['ai-models'] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    {expandedIntegrations['ai-models'] && (
                      <div className="settings-integration-card-body">
                        <p className="setting-description">
                  Add API keys for the AI models you want to use in the Insights advisor. Only models with a configured key will appear in the model selector.
                </p>

                <div className="setting-item">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Sparkles size={14} style={{ color: 'var(--color-info-text)' }} />
                    Gemini API Key
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type={showGeminiKey ? 'text' : 'password'}
                      value={geminiApiKey}
                      onChange={(e) => setGeminiApiKey(e.target.value)}
                      placeholder="AIzaSy..."
                      style={{ flex: 1 }}
                      disabled={savingAiKeys}
                    />
                    <button
                      className="action-button"
                      onClick={() => setShowGeminiKey(!showGeminiKey)}
                      style={{ padding: '8px', minWidth: 36, background: 'transparent', border: '1px solid var(--color-border)' }}
                      title={showGeminiKey ? 'Hide' : 'Show'}
                    >
                      {showGeminiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <span className="setting-description" style={{ marginTop: 4 }}>
                    Get your key from <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-info-text)', fontWeight: 500 }}>Google AI Studio</a>. Free tier available.
                  </span>
                </div>

                <div className="setting-divider"></div>

                <div className="setting-item">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#10a37f" strokeWidth="2" /><path d="M12 7v5l3 3" stroke="#10a37f" strokeWidth="2" strokeLinecap="round" /></svg>
                    OpenAI API Key
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type={showOpenaiKey ? 'text' : 'password'}
                      value={openaiApiKey}
                      onChange={(e) => setOpenaiApiKey(e.target.value)}
                      placeholder="sk-..."
                      style={{ flex: 1 }}
                      disabled={savingAiKeys}
                    />
                    <button
                      className="action-button"
                      onClick={() => setShowOpenaiKey(!showOpenaiKey)}
                      style={{ padding: '8px', minWidth: 36, background: 'transparent', border: '1px solid var(--color-border)' }}
                      title={showOpenaiKey ? 'Hide' : 'Show'}
                    >
                      {showOpenaiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <span className="setting-description" style={{ marginTop: 4 }}>
                    Get your key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-success-text)', fontWeight: 500 }}>OpenAI Platform</a>.
                  </span>
                </div>

                <div className="setting-divider"></div>

                <div className="setting-item">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#d4a574" strokeWidth="2" /><path d="M2 17l10 5 10-5" stroke="#d4a574" strokeWidth="2" /><path d="M2 12l10 5 10-5" stroke="#d4a574" strokeWidth="2" /></svg>
                    Anthropic API Key
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type={showAnthropicKey ? 'text' : 'password'}
                      value={anthropicApiKey}
                      onChange={(e) => setAnthropicApiKey(e.target.value)}
                      placeholder="sk-ant-..."
                      style={{ flex: 1 }}
                      disabled={savingAiKeys}
                    />
                    <button
                      className="action-button"
                      onClick={() => setShowAnthropicKey(!showAnthropicKey)}
                      style={{ padding: '8px', minWidth: 36, background: 'transparent', border: '1px solid var(--color-border)' }}
                      title={showAnthropicKey ? 'Hide' : 'Show'}
                    >
                      {showAnthropicKey ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <span className="setting-description" style={{ marginTop: 4 }}>
                    Get your key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-warning-text)', fontWeight: 500 }}>Anthropic Console</a>.
                  </span>
                </div>

                <div className="setting-divider"></div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                  <button
                    className="action-button primary"
                    disabled={savingAiKeys}
                    onClick={async () => {
                      setSavingAiKeys(true);
                      setAiKeysSaveStatus('idle');
                      try {
                        await setDoc(doc(db, 'appSettings', 'aiModels'), {
                          geminiApiKey: geminiApiKey.trim(),
                          openaiApiKey: openaiApiKey.trim(),
                          anthropicApiKey: anthropicApiKey.trim(),
                        }, { merge: true });
                        setAiKeysSaveStatus('saved');
                        setTimeout(() => setAiKeysSaveStatus('idle'), 3000);
                      } catch (error) {
                        console.error('Error saving AI keys:', error);
                        setAiKeysSaveStatus('error');
                      } finally {
                        setSavingAiKeys(false);
                      }
                    }}
                  >
                    {savingAiKeys ? (
                      <><Loader2 size={14} className="spin-icon" /> Saving...</>
                    ) : (
                      'Save API Keys'
                    )}
                  </button>
                  {aiKeysSaveStatus === 'saved' && (
                    <span style={{ color: 'var(--color-success-text)', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <CheckCircle2 size={14} /> Saved
                    </span>
                  )}
                  {aiKeysSaveStatus === 'error' && (
                    <span style={{ color: 'var(--color-error-text)', fontSize: 13, fontWeight: 500 }}>
                      Error saving keys
                    </span>
                  )}
                </div>

                <div style={{
                  marginTop: 20,
                  padding: '12px 16px',
                  background: 'var(--color-primary-subtle)',
                  border: '1px solid var(--color-success)',
                  borderRadius: 8,
                  fontSize: 13,
                  color: 'var(--color-success-text)',
                  lineHeight: 1.5,
                }}>
                  <strong>Tip:</strong> Only add keys for providers you're actively using. Gemini's free tier is sufficient for most CRM advisor usage.
                </div>
                      </div>
                    )}
                  </div>
                  <div className="settings-integration-card" key="metrics-lite">
                    <button type="button" className="settings-integration-card-header" onClick={() => toggleIntegration('metrics-lite')} aria-expanded={!!expandedIntegrations['metrics-lite']}>
                      <div className="settings-integration-card-title">
                        <strong>Metrics Lite</strong>
                        {(() => {
                    const label = metricsLiteSettings.apiKey ? 'Configured' : 'Not configured';
                    const ok = label !== 'Not configured';
                    return (
                      <span className={`settings-status-chip ${ok ? 'settings-status-chip--ok' : 'settings-status-chip--muted'}`}>{label}</span>
                    );
                  })()}
                      </div>
                      {expandedIntegrations['metrics-lite'] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    {expandedIntegrations['metrics-lite'] && (
                      <div className="settings-integration-card-body">
                        <p className="setting-description">
                  Connect Metrics Lite to automatically sync web traffic data with leads. Visitor data will be enriched with pageviews, goals, UTM parameters, and more.
                </p>

                <div className="setting-item">
                  <label>API URL (Read-Only)</label>
                  <div className="readonly-value" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', padding: '8px 12px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '14px' }}>
                    {metricsLiteSettings.apiUrl}
                    <span style={{ marginLeft: '10px', fontSize: '12px', color: 'var(--color-text-secondary)' }}><Lock size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />Read-only</span>
                  </div>
                  <span className="setting-description">
                    Metrics Lite API URL (hardcoded - same for all users). The system automatically appends lead ID and date range.
                  </span>
                </div>

                <div className="setting-item">
                  <label>API Key</label>
                  <input
                    type="password"
                    value={metricsLiteSettings.apiKey}
                    onChange={(e) => {
                      setMetricsLiteSettings(prev => ({ ...prev, apiKey: e.target.value }));
                    }}
                    onBlur={async (e) => {
                      const newApiKey = e.target.value;
                      if (newApiKey !== metricsLiteSettings.apiKey) {
                        setSavingApiKey(true);
                        try {
                          await saveMetricsLiteApiKey(newApiKey);
                          console.log('Metrics Lite API key saved to Firestore');
                          // Dispatch event to notify components of configuration change
                          window.dispatchEvent(new Event('metricsLiteConfigUpdated'));
                        } catch (error) {
                          console.error('Error saving API key:', error);
                          alert('Failed to save API key. Please try again.');
                        } finally {
                          setSavingApiKey(false);
                        }
                      }
                    }}
                    placeholder="Enter your Metrics Lite API key"
                    disabled={savingApiKey}
                  />
                  {savingApiKey && (
                    <span className="setting-description" style={{ color: 'var(--color-info-text)' }}>
                      Saving...
                    </span>
                  )}
                  <span className="setting-description">
                    Your Metrics Lite API key for authentication. Required to sync web activity data. Saved to database for persistence across deployments.
                  </span>
                </div>

                <div className="setting-item">
                  <label>Test Lead ID</label>
                  <input
                    type="number"
                    value={metricsLiteSettings.testLeadId}
                    onChange={(e) => {
                      console.log('SettingsPanel: Saving test lead ID to localStorage');
                      const newSettings = { ...metricsLiteSettings, testLeadId: e.target.value };
                      setMetricsLiteSettings(newSettings);
                      localStorage.setItem('metrics_lite_test_lead_id', e.target.value);

                      // Verify it was saved
                      const saved = localStorage.getItem('metrics_lite_test_lead_id');
                      console.log('SettingsPanel: Verified saved test lead ID:', saved);
                    }}
                    placeholder="Enter a lead ID to test with"
                  />
                  <span className="setting-description">
                    Lead ID to use for testing the API connection. Should be a visitor ID that exists in your Metrics Lite account.
                  </span>
                </div>

                <div className="setting-item">
                  <div className="setting-status">
                    <strong>Status:</strong>{' '}
                    {metricsLiteSettings.apiUrl && metricsLiteSettings.apiKey ? (
                      <span style={{ color: 'var(--color-success-text)' }}>✓ Configured</span>
                    ) : (
                      <span style={{ color: 'var(--color-error-text)' }}>✗ Not configured</span>
                    )}
                  </div>
                  <p className="setting-description">
                    {metricsLiteSettings.apiUrl && metricsLiteSettings.apiKey
                      ? 'Metrics Lite is ready to sync visitor data. You can link visitors to leads from the lead details panel.'
                      : 'Enter your API URL and key to enable Metrics Lite integration.'}
                  </p>
                  {metricsLiteSettings.apiUrl && metricsLiteSettings.apiKey && (
                    <button
                      onClick={async () => {
                        setMetricsLiteTesting(true);
                        setMetricsLiteTestResult(null);

                        // Update client with current settings
                        metricsLiteClient.updateCredentials(metricsLiteSettings.apiUrl, metricsLiteSettings.apiKey);

                        try {
                          // Test with configurable visitor ID (defaults to 34502 if invalid)
                          const testVisitorId = parseInt(metricsLiteSettings.testLeadId) || 34502;
                          const visitor = await metricsLiteClient.getComprehensiveLeadData(testVisitorId);

                          if (visitor) {
                            setMetricsLiteTestResult({
                              success: true,
                              message: `✓ Connection successful! Found visitor ${testVisitorId} with ${visitor.statistics?.pageviews || 0} pageviews.`
                            });
                          } else {
                            setMetricsLiteTestResult({
                              success: false,
                              message: `✗ Connection successful but no data found for visitor ${testVisitorId}. Verify the visitor ID exists.`
                            });
                          }
                        } catch (error: any) {
                          setMetricsLiteTestResult({
                            success: false,
                            message: `✗ Connection failed: ${error.response?.data?.error || error.message || 'Unknown error'}`
                          });
                        } finally {
                          setMetricsLiteTesting(false);
                        }
                      }}
                      disabled={metricsLiteTesting}
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        backgroundColor: '#4285F4',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: metricsLiteTesting ? 'not-allowed' : 'pointer',
                        opacity: metricsLiteTesting ? 0.6 : 1
                      }}
                    >
                      {metricsLiteTesting ? 'Testing...' : 'Test Connection'}
                    </button>
                  )}
                  {metricsLiteTestResult && (
                    <p style={{
                      marginTop: '8px',
                      padding: '8px',
                      backgroundColor: metricsLiteTestResult.success ? '#E8F5E9' : '#FFEBEE',
                      color: metricsLiteTestResult.success ? '#2E7D32' : '#C62828',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      {metricsLiteTestResult.message}
                    </p>
                  )}
                </div>

                <div className="setting-divider"></div>

                <div className="setting-item">
                  <h4>How it works:</h4>
                  <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                    <li>Enter a visitor ID in a lead's Metrics Lite field</li>
                    <li>Click "Sync from Metrics Lite" to fetch visitor data</li>
                    <li>Visitor data (pageviews, goals, UTM params) will be synced to the lead</li>
                    <li>Use "Search Visitors" to find visitors by email, name, or IP</li>
                  </ul>
                </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {section === 'proposals' && (
              <div className="settings-section">
                <h3>Proposal Rate Settings</h3>
                <p className="setting-description" style={{ marginBottom: '24px' }}>
                  Configure default rates used in proposal calculations. These rates are applied to all new proposals.
                </p>

                <h4 style={{ marginTop: '16px', marginBottom: '12px' }}>Overhead Rate</h4>
                <div className="setting-item">
                  <label>Daily Overhead Rate ($/day)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.overheadDailyRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      overheadDailyRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                  <span className="setting-description">Applied per overhead day and per install day</span>
                </div>

                <div className="setting-divider"></div>

                <h4 style={{ marginTop: '16px', marginBottom: '12px' }}>Design Rates</h4>
                <div className="setting-item">
                  <label>Project Manager Rate ($/hr)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.pmRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      pmRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>APM Rate ($/hr)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.apmRate ?? 40}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      apmRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Lead Designer Rate ($/hr)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.leadDesignerRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      leadDesignerRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Support Designer Rate ($/hr)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.supportDesignerRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      supportDesignerRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Engineering Rate ($/hr)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.engineeringRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      engineeringRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>

                <div className="setting-divider"></div>

                <h4 style={{ marginTop: '16px', marginBottom: '12px' }}>Install Rates</h4>
                <div className="setting-item">
                  <label>Team Labor Rate ($/day)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.teamLaborDailyRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      teamLaborDailyRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Crane Rate ($/day)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.craneDailyRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      craneDailyRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Crane Rate ($/hour)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.craneHourlyRate ?? 0}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      craneHourlyRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Crane OT Rate ($/hour)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.craneOvertimeHourlyRate ?? 0}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      craneOvertimeHourlyRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Telehandler Rate ($/day)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.telehandlerDailyRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      telehandlerDailyRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Scissor Lift Rate ($/day)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.scissorLiftDailyRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      scissorLiftDailyRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Basket Lift Rate ($/day)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.basketLiftDailyRate ?? 1500}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      basketLiftDailyRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>

                <div className="setting-divider"></div>

                <h4 style={{ marginTop: '16px', marginBottom: '12px' }}>Production & Materials</h4>
                <div className="setting-item">
                  <label>Production Rate ($/bdft)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={proposalRates.productionRatePerBdft}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      productionRatePerBdft: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Post Base Unit Cost ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.postBaseUnitCost}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      postBaseUnitCost: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>

                <div className="setting-divider"></div>

                <h4 style={{ marginTop: '16px', marginBottom: '12px' }}>Travel</h4>
                <div className="setting-item">
                  <label>Mileage Rate ($/mile)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={proposalRates.mileageRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      mileageRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Per Diem Rate ($/person/day)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.perDiemRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      perDiemRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Default Hotel Rate ($/night)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.defaultHotelRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      defaultHotelRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="setting-item">
                  <label>Travel Day Rate ($/day)</label>
                  <input
                    type="number"
                    min="0"
                    value={proposalRates.travelDayRate}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      travelDayRate: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                  <span className="setting-description">Billed for each travel day to/from job site</span>
                </div>

                <div className="setting-divider"></div>

                <h4 style={{ marginTop: '16px', marginBottom: '12px' }}>Default Profit Margin</h4>
                <div className="setting-item">
                  <label>Default Profit Margin (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={proposalRates.defaultProfitMargin}
                    onChange={(e) => setProposalRates(prev => ({
                      ...prev,
                      defaultProfitMargin: parseFloat(e.target.value) || 0
                    }))}
                    style={{ width: '120px' }}
                  />
                </div>

                <div style={{ marginTop: '24px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <button
                    className="save-button"
                    disabled={savingProposalRates}
                    onClick={async () => {
                      setSavingProposalRates(true);
                      try {
                        await saveProposalRates(proposalRates);
                        setProposalRatesSaveStatus('saved');
                        setTimeout(() => setProposalRatesSaveStatus('idle'), 3000);
                      } catch (error) {
                        console.error('Error saving proposal rates:', error);
                        setProposalRatesSaveStatus('error');
                      } finally {
                        setSavingProposalRates(false);
                      }
                    }}
                  >
                    {savingProposalRates ? 'Saving...' : 'Save Proposal Rates'}
                  </button>
                  {proposalRatesSaveStatus === 'saved' && (
                    <span style={{ color: 'var(--color-success-text)' }}>✓ Saved successfully</span>
                  )}
                  {proposalRatesSaveStatus === 'error' && (
                    <span style={{ color: 'var(--color-error-text)' }}>Failed to save</span>
                  )}
                  <button
                    className="cancel-button"
                    onClick={() => setProposalRates(DEFAULT_PROPOSAL_RATES)}
                    style={{ marginLeft: 'auto' }}
                  >
                    Reset to Defaults
                  </button>
                </div>
              </div>
            )}

            {section === 'admin' && (
              <div className="settings-section">
                <PeopleHubBenefitsAdminSettings />
              </div>
            )}

            {section === 'crm' && (
              <div className="settings-section">
                <h3>CRM</h3>
                <section className="settings-ops-section" aria-label="Lead Source Colors">
                  <h4 className="settings-ops-section-title">Lead Source Colors</h4>
                  <p className="setting-description">
                  Customize the colors for each lead source. These colors will be used for map markers and source badges throughout the CRM.
                </p>

                <div className="source-colors-list">
                  {allSources.map(source => {
                    const currentColor = sourceColors[source];
                    const isDefault = currentColor === DEFAULT_SOURCE_COLORS[source];

                    return (
                      <div key={source} className="source-color-item">
                        <div className="source-color-info">
                          <span
                            className="source-color-preview"
                            style={{ backgroundColor: currentColor }}
                            title={`${formatSourceName(source)} - ${currentColor}`}
                          />
                          <span className="source-color-label">{formatSourceName(source)}</span>
                        </div>
                        <div className="source-color-controls">
                          <input
                            type="color"
                            value={currentColor}
                            onChange={(e) => handleSourceColorChange(source, e.target.value)}
                            className="color-picker"
                          />
                          {!isDefault && (
                            <button
                              type="button"
                              onClick={() => handleResetSourceColor(source)}
                              className="reset-color-button"
                              title="Reset to default"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="source-colors-actions">
                  <button
                    type="button"
                    onClick={handleResetAllColors}
                    className="reset-all-button"
                  >
                    Reset All to Defaults
                  </button>
                </div>
                </section>
                <section className="settings-ops-section" aria-label="Call Automation">
                  <h4 className="settings-ops-section-title">Call Automation</h4>
                  <p className="setting-description">
                  Configure automatic call scheduling for new leads. When a lead comes in, an initial call activity
                  will be created automatically. If the call is unanswered, follow-up calls are scheduled based on these intervals.
                </p>

                <div className="setting-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={automationSettings.autoCreateInitialCall}
                      onChange={(e) => setAutomationSettings(prev => ({
                        ...prev,
                        autoCreateInitialCall: e.target.checked
                      }))}
                    />
                    <span>Automatically create initial call activity for new leads</span>
                  </label>
                </div>

                <div className="setting-divider"></div>

                <h4 style={{ marginTop: '16px', marginBottom: '12px' }}>Call Intervals</h4>

                <div className="setting-item">
                  <label>1st Call (after lead creation)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={automationSettings.firstCallInterval}
                      onChange={(e) => setAutomationSettings(prev => ({
                        ...prev,
                        firstCallInterval: parseInt(e.target.value) || 1
                      }))}
                      style={{ width: '80px' }}
                    />
                    <span>hour(s)</span>
                  </div>
                </div>

                <div className="setting-item">
                  <label>2nd Call (if no answer)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="number"
                      min="1"
                      max="48"
                      value={automationSettings.secondCallInterval}
                      onChange={(e) => setAutomationSettings(prev => ({
                        ...prev,
                        secondCallInterval: parseInt(e.target.value) || 2
                      }))}
                      style={{ width: '80px' }}
                    />
                    <span>hour(s) after 1st attempt</span>
                  </div>
                </div>

                <div className="setting-item">
                  <label>3rd Call (if no answer)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={automationSettings.thirdCallInterval}
                      onChange={(e) => setAutomationSettings(prev => ({
                        ...prev,
                        thirdCallInterval: parseInt(e.target.value) || 24
                      }))}
                      style={{ width: '80px' }}
                    />
                    <span>hour(s) after 2nd attempt</span>
                  </div>
                  <span className="setting-hint">(24 hours = 1 day)</span>
                </div>

                <div className="setting-item">
                  <label>4th Call (if no answer)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="number"
                      min="1"
                      max="336"
                      value={automationSettings.fourthCallInterval}
                      onChange={(e) => setAutomationSettings(prev => ({
                        ...prev,
                        fourthCallInterval: parseInt(e.target.value) || 72
                      }))}
                      style={{ width: '80px' }}
                    />
                    <span>hour(s) after 3rd attempt</span>
                  </div>
                  <span className="setting-hint">(72 hours = 3 days)</span>
                </div>

                <div className="setting-divider"></div>

                <h4 style={{ marginTop: '16px', marginBottom: '12px' }}>Working Hours</h4>
                <p className="setting-description">
                  Calls will only be scheduled during working hours. If a call is due outside these hours,
                  it will be scheduled for the next available working time.
                </p>

                <div className="setting-item">
                  <label>Working Hours</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <select
                      value={automationSettings.workingHoursStart}
                      onChange={(e) => setAutomationSettings(prev => ({
                        ...prev,
                        workingHoursStart: parseInt(e.target.value)
                      }))}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                        </option>
                      ))}
                    </select>
                    <span>to</span>
                    <select
                      value={automationSettings.workingHoursEnd}
                      onChange={(e) => setAutomationSettings(prev => ({
                        ...prev,
                        workingHoursEnd: parseInt(e.target.value)
                      }))}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="setting-item">
                  <label>Working Days</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                      <label key={day} className="checkbox-label" style={{ margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={automationSettings.workingDays.includes(index)}
                          onChange={(e) => {
                            const newDays = e.target.checked
                              ? [...automationSettings.workingDays, index].sort()
                              : automationSettings.workingDays.filter(d => d !== index);
                            setAutomationSettings(prev => ({
                              ...prev,
                              workingDays: newDays
                            }));
                          }}
                        />
                        <span>{day}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="setting-divider"></div>

                <h4 style={{ marginTop: '16px', marginBottom: '12px' }}>Lost Lead Handling</h4>

                <div className="setting-item">
                  <label>Maximum Call Attempts</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={automationSettings.maxAttempts}
                      onChange={(e) => setAutomationSettings(prev => ({
                        ...prev,
                        maxAttempts: parseInt(e.target.value) || 4
                      }))}
                      style={{ width: '80px' }}
                    />
                    <span>attempts before prompting to mark as lost</span>
                  </div>
                </div>

                <div className="setting-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={automationSettings.autoMarkLost}
                      onChange={(e) => setAutomationSettings(prev => ({
                        ...prev,
                        autoMarkLost: e.target.checked
                      }))}
                    />
                    <span>Prompt to mark lead as lost after max attempts</span>
                  </label>
                </div>
                  <div style={{ marginTop: 16 }}>
                    <button type="button" className="action-button primary" onClick={() => {
                      localStorage.setItem('crm_automation_settings', JSON.stringify(automationSettings));
                    }}>Save automation settings</button>
                  </div>
                </section>
              </div>
            )}

            

            

            {section === 'team' && (
              <div className="settings-section">
                <h3>Team</h3>
                <section className="settings-ops-section" aria-label="Users">
                  <h4 className="settings-ops-section-title">Users</h4>
                  <div className="settings-section" style={{ maxWidth: '100%' }}>
                    <UserManagement />
                  </div>
                </section>
              </div>
            )}

            {section === 'tools' && (
              <div className="settings-section">
                <h3>Hand cut vs CNC</h3>
                <p className="setting-description">
                  Optional external link (spreadsheet, SOP, or supplier portal) shown on the Tools page next to the in-app calculator.
                </p>
                <div className="setting-item">
                  <label>Comparison tool URL (optional)</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="url"
                      value={handCutVsCncComparisonUrl}
                      onChange={(e) => setHandCutVsCncComparisonUrl(e.target.value)}
                      placeholder="https://…"
                      style={{ flex: 1, minWidth: '200px' }}
                      disabled={handCutVsCncUrlSaving}
                    />
                    <button
                      type="button"
                      className="action-button primary"
                      disabled={handCutVsCncUrlSaving}
                      onClick={async () => {
                        setHandCutVsCncUrlSaving(true);
                        try {
                          await updateSetting('handCutVsCncComparisonUrl', handCutVsCncComparisonUrl.trim());
                        } catch (err) {
                          console.error(err);
                          alert('Failed to save URL');
                        } finally {
                          setHandCutVsCncUrlSaving(false);
                        }
                      }}
                    >
                      {handCutVsCncUrlSaving ? 'Saving…' : 'Save URL'}
                    </button>
                  </div>
                </div>

                <div className="setting-divider" />

                <h3>Data Tools</h3>
                <p className="setting-description">
                  Import, analyze, and clean up your CRM data.
                </p>

                <div className="tools-links">
                  <a href="/import/tools" className="tool-link-card">
                    <div className="tool-icon"><Wrench size={20} /></div>
                    <div className="tool-info">
                      <div className="tool-name">Lead Analysis Tools</div>
                      <div className="tool-desc">Find duplicates, fix data quality issues, bulk geocode leads</div>
                    </div>
                    <span className="tool-arrow">→</span>
                  </a>

                  <a href="/import/meta-ads" className="tool-link-card">
                    <div className="tool-icon"><Smartphone size={20} /></div>
                    <div className="tool-info">
                      <div className="tool-name">Meta Ads Import</div>
                      <div className="tool-desc">Import leads from Facebook & Instagram ad campaigns</div>
                    </div>
                    <span className="tool-arrow">→</span>
                  </a>

                  <a href="/import/pipedrive" className="tool-link-card">
                    <div className="tool-icon"><BarChart3 size={20} /></div>
                    <div className="tool-info">
                      <div className="tool-name">Pipedrive Import</div>
                      <div className="tool-desc">Import people, leads, and deals from Pipedrive CSV exports</div>
                    </div>
                    <span className="tool-arrow">→</span>
                  </a>

                  <a href="/import" className="tool-link-card">
                    <div className="tool-icon"><Inbox size={20} /></div>
                    <div className="tool-info">
                      <div className="tool-name">Generic CSV Import</div>
                      <div className="tool-desc">Import leads from any CSV file with flexible column mapping</div>
                    </div>
                    <span className="tool-arrow">→</span>
                  </a>
                </div>

                <div className="setting-divider"></div>

                <h3>Batch Geocode Leads</h3>
                <p className="setting-description">
                  Retry geocoding for all leads stuck at the default map location or missing coordinates.
                  This uses the Google Maps Geocoding API to resolve each lead's location text into map coordinates.
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                  <button
                    className="action-button primary"
                    disabled={geocodeRunning}
                    onClick={async () => {
                      setGeocodeRunning(true);
                      setGeocodeResult(null);
                      setGeocodeError(null);
                      try {
                        const resp = await fetch(
                          'https://us-central1-lctf-projects.cloudfunctions.net/batchGeocode',
                          { method: 'POST' }
                        );
                        const data = await resp.json();
                        if (data.success) {
                          setGeocodeResult({ needed: data.needed, geocoded: data.geocoded, failed: data.failed, total: data.total });
                        } else {
                          setGeocodeError(data.error || 'Unknown error');
                        }
                      } catch (err: any) {
                        setGeocodeError(err.message || 'Network error');
                      } finally {
                        setGeocodeRunning(false);
                      }
                    }}
                  >
                    {geocodeRunning ? (
                      <><Loader2 size={16} className="spin-icon" /> Geocoding...</>
                    ) : (
                      <><MapPin size={16} /> Run Batch Geocode</>
                    )}
                  </button>
                </div>

                {geocodeResult && (
                  <div style={{
                    marginTop: 12,
                    padding: '12px 16px',
                    borderRadius: 8,
                    background: geocodeResult.failed === 0 ? '#f0fdf4' : '#fffbeb',
                    border: `1px solid ${geocodeResult.failed === 0 ? '#bbf7d0' : '#fde68a'}`,
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                  }}>
                    {geocodeResult.failed === 0 ? (
                      <CheckCircle2 size={18} style={{ color: 'var(--color-success-text)', flexShrink: 0, marginTop: 1 }} />
                    ) : (
                      <AlertTriangle size={18} style={{ color: 'var(--color-warning-text)', flexShrink: 0, marginTop: 1 }} />
                    )}
                    <div>
                      <strong>{geocodeResult.geocoded}</strong> of <strong>{geocodeResult.needed}</strong> leads geocoded successfully.
                      {geocodeResult.failed > 0 && (
                        <span style={{ color: 'var(--color-warning-text)' }}> {geocodeResult.failed} failed (location text may be too vague).</span>
                      )}
                      {geocodeResult.needed === 0 && (
                        <span style={{ color: 'var(--color-success-text)' }}> All {geocodeResult.total} leads already have valid coordinates.</span>
                      )}
                    </div>
                  </div>
                )}

                {geocodeError && (
                  <div style={{
                    marginTop: 12,
                    padding: '12px 16px',
                    borderRadius: 8,
                    background: 'var(--color-error-light)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-error-text)',
                    fontSize: 14,
                  }}>
                    Error: {geocodeError}
                  </div>
                )}
              </div>
            )}

            {section === 'api' && (
              <div className="settings-section api-settings">
                <div className="settings-section-header" style={{ marginBottom: 16, paddingBottom: 0, border: 'none' }}>
                  <div>
                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Sparkles size={18} /> API & Agents</h3>
                    <p className="setting-description">Configure the Mac-mini agent gateway and catalog.</p>
                  </div>
                  {copyFeedback && <span className="settings-save-feedback settings-save-feedback--saved">{copyFeedback}</span>}
                </div>
                <div className="settings-api-cards">
                  <div className="settings-api-card">
                    <div className="settings-api-card-header"><strong>Mac-mini agents</strong>
                      <span className={`settings-status-chip ${agentApiKey ? 'settings-status-chip--ok' : 'settings-status-chip--muted'}`}>{agentApiKey ? 'Secured' : 'Not set'}</span>
                    </div>
                    <p className="setting-description">Deals, marketing, sales, and all sync scripts use one gateway key.</p>
                  </div>
                </div>
                {inlineFeedback && <p className="settings-save-feedback settings-save-feedback--saved">{inlineFeedback}</p>}
                <div className="setting-divider"></div>

                <h3 style={{ marginTop: 24 }}>CRM Lead API</h3>
                <div className="api-key-section">
                  <h4>API Key</h4>
                  <div className="setting-item">
                    <label>API Key</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type={revealCrmApiKey ? 'text' : 'password'}
                        value={crmApiKey}
                        onChange={(e) => setCrmApiKey(e.target.value)}
                        placeholder="Leave empty for open API (no auth required)"
                        style={{ flex: 1, minWidth: 200 }}
                        disabled={savingCrmApiKey}
                      />
                      <button type="button" className="settings-btn-secondary" onClick={() => setRevealCrmApiKey(!revealCrmApiKey)}>
                        {revealCrmApiKey ? 'Hide' : 'Reveal'}
                      </button>
                      <button
                        type="button"
                        className="settings-btn-primary"
                        disabled={savingCrmApiKey}
                        onClick={async () => {
                          setSavingCrmApiKey(true);
                          try {
                            await saveCrmApiKey(crmApiKey);
                            showInlineFeedback(crmApiKey ? 'CRM Lead API key saved.' : 'CRM Lead API key removed.');
                          } catch (error) {
                            showInlineFeedback('Failed to save CRM Lead API key');
                            console.error(error);
                          } finally {
                            setSavingCrmApiKey(false);
                          }
                        }}
                      >
                        {savingCrmApiKey ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                  <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="settings-btn-secondary"
                      disabled={!crmApiKey.trim()}
                      onClick={() => { void navigator.clipboard.writeText(crmApiKey.trim()).then(() => showCopyFeedback('CRM Lead API key copied')); }}
                    >
                      Copy key
                    </button>
                    <button
                      type="button"
                      className="settings-btn-secondary"
                      disabled={!crmApiKey.trim()}
                      onClick={() => { void navigator.clipboard.writeText(`Bearer ${crmApiKey.trim()}`).then(() => showCopyFeedback('Bearer header copied')); }}
                    >
                      Copy Bearer header
                    </button>
                  </div>
                </div>

                <div className="setting-divider"></div>

                <h3 style={{ marginTop: 24 }}>Agent Gateway API</h3>
                <p className="setting-description">
                  One URL and one key for all Mac-mini agents. POST to{' '}
                  <code>agentGatewayApi</code> with <code>{'{ agent, action, ...params }'}</code>.
                </p>
                <div className="api-key-section">
                  <h4>Agent API Key</h4>
                  <div className="setting-item">
                    <label>API Key</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type={revealAgentApiKey ? 'text' : 'password'}
                        value={agentApiKey}
                        onChange={e => setAgentApiKey(e.target.value)}
                        placeholder="Generate a long random string"
                        style={{ flex: 1, minWidth: 200 }}
                        disabled={savingAgentApiKey}
                      />
                      <button type="button" className="settings-btn-secondary" onClick={() => setRevealAgentApiKey(!revealAgentApiKey)}>
                        {revealAgentApiKey ? 'Hide' : 'Reveal'}
                      </button>
                      <button
                        type="button"
                        className="settings-btn-secondary"
                        onClick={() => {
                          const bytes = crypto.getRandomValues(new Uint8Array(32));
                          setAgentApiKey(Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join(''));
                        }}
                      >
                        Generate
                      </button>
                      <button
                        type="button"
                        className="settings-btn-primary"
                        disabled={savingAgentApiKey}
                        onClick={async () => {
                          setSavingAgentApiKey(true);
                          try {
                            await saveAgentApiKey(agentApiKey);
                            showInlineFeedback(agentApiKey ? 'Agent API key saved.' : 'Agent API key removed.');
                          } finally {
                            setSavingAgentApiKey(false);
                          }
                        }}
                      >
                        {savingAgentApiKey ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                  <p className="setting-description">Gateway URL: <code>{AGENT_GATEWAY_ENDPOINT}</code></p>
                  <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button type="button" className="settings-btn-secondary" onClick={() => { void navigator.clipboard.writeText(AGENT_GATEWAY_ENDPOINT).then(() => showCopyFeedback('Gateway URL copied')); }}>
                      Copy gateway URL
                    </button>
                    <button type="button" className="settings-btn-secondary" disabled={!agentApiKey.trim()} onClick={() => { void navigator.clipboard.writeText(`Bearer ${agentApiKey.trim()}`).then(() => showCopyFeedback('Bearer header copied')); }}>
                      Copy Bearer header
                    </button>
                    <button type="button" className="settings-btn-secondary" onClick={() => window.open('/docs/agents/catalog.json', '_blank', 'noopener,noreferrer')}>
                      Agent docs
                    </button>
                  </div>
                  <button
                    type="button"
                    className="settings-btn-secondary"
                    style={{ marginTop: '8px' }}
                    disabled={agentGatewayTesting || !agentApiKey.trim()}
                    onClick={async () => {
                      setAgentGatewayTesting(true);
                      setAgentGatewayTestResult(null);
                      try {
                        const res = await fetch(AGENT_GATEWAY_ENDPOINT, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${agentApiKey.trim()}` },
                          body: JSON.stringify({ action: 'status' }),
                        });
                        const data = await res.json();
                        setAgentGatewayTestResult({
                          success: res.ok && data.success,
                          message: data.success ? `Gateway OK — ${(data.agents || []).length} agents` : data.error || `HTTP ${res.status}`,
                        });
                      } catch (e) {
                        setAgentGatewayTestResult({ success: false, message: (e as Error).message });
                      } finally {
                        setAgentGatewayTesting(false);
                      }
                    }}
                  >
                    {agentGatewayTesting ? 'Testing…' : 'Test gateway'}
                  </button>
                  {agentGatewayTestResult && (
                    <p className={agentGatewayTestResult.success ? 'setting-success' : 'setting-error'}>{agentGatewayTestResult.message}</p>
                  )}
                </div>

                <div className="setting-divider"></div>

                <h3 style={{ marginTop: 24 }}>CompanyBrain repo</h3>
                <p className="setting-description">
                  Agent Activity and Company Brain search read LC-Timberframes/company_brain. Paste a GitHub token with Contents access if Functions GITHUB_TOKEN cannot see the org.
                </p>
                <CompanyBrainRepoCard />

                <div className="setting-divider"></div>

                <h3><Sparkles size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Agent Catalog</h3>
                <p className="setting-description">
                  Mac-mini agents pull API contracts from the gateway — no manual .md transfer.
                  Sync writes to <code>CompanyBrain/System/agent-catalog.json</code>.
                </p>
                <div className="setting-item" style={{ marginTop: '12px' }}>
                  <label>Gateway endpoint</label>
                  <div className="api-code-block">
                    <code>{AGENT_GATEWAY_ENDPOINT}</code>
                    <button type="button" className="copy-btn" onClick={() => { navigator.clipboard.writeText(AGENT_GATEWAY_ENDPOINT); showCopyFeedback('Endpoint copied'); }}>Copy</button>
                  </div>
                  <p className="setting-description" style={{ marginTop: '6px' }}>
                    POST <code>{'{ "action": "catalog" }'}</code> or{' '}
                    <code>{'{ "agent": "dealsAgent", "action": "status" }'}</code> · Hosted JSON:{' '}
                    <a href={AGENT_CATALOG_JSON_URL} target="_blank" rel="noopener noreferrer">/docs/agents/catalog.json</a>
                  </p>
                </div>
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="action-button"
                    disabled={agentCatalogLoading || !agentApiKey.trim()}
                    onClick={async () => {
                      setAgentCatalogLoading(true);
                      try {
                        const res = await fetch(AGENT_GATEWAY_ENDPOINT, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${agentApiKey.trim()}` },
                          body: JSON.stringify({ action: 'catalog' }),
                        });
                        const data = await res.json();
                        if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
                        const apis = Array.isArray(data.apis) ? (data.apis as { id?: string }[]).map(a => a.id).filter(Boolean) : [];
                        setAgentCatalogInfo({
                          version: String(data.catalogVersion || ''),
                          hash: String(data.catalogHash || '').slice(0, 20) + '…',
                          apis: apis as string[],
                        });
                      } catch (err) {
                        showInlineFeedback(`Catalog fetch failed: ${(err as Error).message}`);
                      } finally {
                        setAgentCatalogLoading(false);
                      }
                    }}
                  >
                    {agentCatalogLoading ? 'Fetching…' : 'Refresh catalog'}
                  </button>
                  <button type="button" className="action-button" onClick={() => window.open('/docs/agents/catalog.json', '_blank', 'noopener,noreferrer')}>
                    View catalog JSON
                  </button>
                </div>
                {agentCatalogInfo && (
                  <p className="setting-description" style={{ marginTop: '8px' }}>
                    Version <strong>{agentCatalogInfo.version}</strong> · APIs: {agentCatalogInfo.apis?.join(', ')}
                  </p>
                )}

                <div className="setting-divider"></div>

                <h3>Bidding agent identity</h3>
                <div className="api-key-section" style={{ marginTop: '12px' }}>
                  <h4>CRM attribution</h4>
                  <p className="setting-description">
                    Attribution for document synopses and markup pins the bidding agent writes. Annotations authored
                    with this identity are styled as agent output and skip Slack notifications.
                  </p>
                  <div className="setting-item">
                    <label>Display name</label>
                    <input type="text" value={biddingAgentDisplayName} onChange={e => setBiddingAgentDisplayName(e.target.value)} placeholder="Bidding Agent" disabled={savingBiddingAgentIdentity} />
                  </div>
                  <div className="setting-item">
                    <label>User ID</label>
                    <input type="text" value={biddingAgentUserId} onChange={e => setBiddingAgentUserId(e.target.value)} placeholder="bidding-agent" disabled={savingBiddingAgentIdentity} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="action-button primary"
                      disabled={savingBiddingAgentIdentity}
                      onClick={async () => {
                        setSavingBiddingAgentIdentity(true);
                        try {
                          await saveBiddingAgentIdentity({ userId: biddingAgentUserId.trim(), displayName: biddingAgentDisplayName.trim() });
                          alert('Bidding agent identity saved.');
                        } catch (error) {
                          showInlineFeedback('Failed to save agent identity');
                          console.error(error);
                        } finally {
                          setSavingBiddingAgentIdentity(false);
                        }
                      }}
                    >
                      {savingBiddingAgentIdentity ? 'Saving...' : 'Save Identity'}
                    </button>
                  </div>
                </div>

              </div>
            )}

            {section === 'client-experience' && (
              <div className="settings-section">
                <h3>Client Experience</h3>
                <section className="settings-ops-section" aria-label="Engagement Documents">
                  <h4 className="settings-ops-section-title">Engagement Documents</h4>
                  <p className="setting-description">
                  Configure assets and documents for client-facing engagement letters.
                </p>

                {/* Logo Selection */}
                <div className="setting-item">
                  <h4><Image size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Company Logo</h4>
                  <p className="setting-description">
                    This logo will appear in the header of engagement letters.
                  </p>
                  {engagementLogo ? (
                    <div className="selected-media-preview">
                      <img src={engagementLogo.url} alt={engagementLogo.name} className="media-thumbnail" />
                      <span className="media-name">{engagementLogo.name}</span>
                      <button
                        className="remove-media-btn"
                        onClick={() => setEngagementLogo(null)}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      className="select-media-btn"
                      onClick={() => setShowLogoPicker(true)}
                    >
                      <Upload size={16} />
                      Select Logo
                    </button>
                  )}
                </div>

                <div className="setting-divider"></div>

                {/* Background Image */}
                <div className="setting-item">
                  <h4><Image size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Background Image</h4>
                  <p className="setting-description">
                    This image will appear as a blurred background on engagement letters.
                  </p>
                  {engagementBackground ? (
                    <div className="selected-media-preview">
                      <img src={engagementBackground.url} alt={engagementBackground.name} className="media-thumbnail" />
                      <span className="media-name">{engagementBackground.name}</span>
                      <button
                        className="remove-media-btn"
                        onClick={() => setEngagementBackground(null)}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      className="select-media-btn"
                      onClick={() => setShowBackgroundPicker(true)}
                    >
                      <Upload size={16} />
                      Select Background
                    </button>
                  )}
                </div>

                <div className="setting-divider"></div>

                {/* Sample Plans Image */}
                <div className="setting-item">
                  <h4><Image size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Sample Plans Image</h4>
                  <p className="setting-description">
                    Upload an image of sample plans to show in the Scope of Services section.
                  </p>
                  {samplePlansImage ? (
                    <div className="selected-media-preview">
                      <img src={samplePlansImage.url} alt={samplePlansImage.name} className="media-thumbnail" />
                      <span className="media-name">{samplePlansImage.name}</span>
                      <button
                        className="remove-media-btn"
                        onClick={() => setSamplePlansImage(null)}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      className="select-media-btn"
                      onClick={() => setShowSamplePlansPicker(true)}
                    >
                      <Upload size={16} />
                      Select Sample Plans Image
                    </button>
                  )}
                </div>

                <div className="setting-divider"></div>

                {/* Engagement Video */}
                <div className="setting-item">
                  <h4><span style={{ fontSize: '16px', marginRight: 8 }}>🎥</span>Engagement Video</h4>
                  <p className="setting-description">
                    YouTube URL for the engagement letter video (use embed link format).
                  </p>
                  <input
                    type="url"
                    value={engagementVideoUrl}
                    onChange={(e) => setEngagementVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/embed/KjUTg0VuVPg"
                    style={{ width: '100%' }}
                  />
                </div>

                <div className="setting-divider"></div>

                {/* Retainer Settings */}
                <div className="setting-item">
                  <h4><span style={{ fontSize: '16px', marginRight: 8 }}>💰</span>Retainer Settings</h4>
                  <label>Total Estimated Fee Range</label>
                  <input
                    type="text"
                    value={retainerTotalFeeRange}
                    onChange={(e) => setRetainerTotalFeeRange(e.target.value)}
                    placeholder="$4,500 - $6,000"
                    style={{ width: '100%' }}
                  />
                  <p className="setting-hint">
                    Displayed range for "Total Estimated Fee" in retainer letters.
                  </p>
                </div>

                <div className="setting-divider"></div>

                {/* Payment Link Assignments */}
                <div className="setting-item">
                  <h4><span style={{ fontSize: '16px', marginRight: 8 }}>🔗</span>Payment Link Assignments</h4>
                  <p className="setting-description">
                    Assign specific payment links to engagement letter types.
                  </p>

                  <div style={{ marginBottom: '12px' }}>
                    <label>Concept Payment Link ($500)</label>
                    <select
                      value={conceptPaymentLinkId}
                      onChange={(e) => setConceptPaymentLinkId(e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="">-- Select a Link --</option>
                      {quickbooksSettings.paymentLinks.map(link => (
                        <option key={link.id} value={link.id}>{link.name} ({link.url})</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label>Retainer Payment Link ($1,600)</label>
                    <select
                      value={retainerPaymentLinkId}
                      onChange={(e) => setRetainerPaymentLinkId(e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="">-- Select a Link --</option>
                      {quickbooksSettings.paymentLinks.map(link => (
                        <option key={link.id} value={link.id}>{link.name} ({link.url})</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="setting-divider"></div>

                {/* Client Documents */}
                <div className="setting-item">
                  <h4><FileText size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Client Documents</h4>
                  <p className="setting-description">
                    Add PDF documents that clients can access from engagement letters (e.g., brochures, guides, terms).
                  </p>

                  {engagementDocuments.length > 0 && (
                    <div className="documents-list">
                      {engagementDocuments.map((doc, index) => (
                        <div key={doc.id || index} className="document-item">
                          <FileText size={16} />
                          <span className="doc-name">{doc.name}</span>
                          <a href={doc.url} target="_blank" rel="noopener noreferrer" className="doc-link">
                            View
                          </a>
                          <button
                            className="remove-doc-btn"
                            onClick={() => {
                              setEngagementDocuments(prev => prev.filter((_, i) => i !== index));
                            }}
                            title="Remove"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    className="select-media-btn"
                    onClick={() => setShowDocPicker(true)}
                    style={{ marginTop: engagementDocuments.length > 0 ? 12 : 0 }}
                  >
                    <Upload size={16} />
                    Add Document
                  </button>
                </div>

                <div className="setting-divider"></div>

                {/* Portal Appearance */}
                <div className="setting-item">
                  <h4><Palette size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Client Portal Appearance</h4>
                  <p className="setting-description">
                    Customize the look of the public client portal page.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Overlay Color */}
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        Overlay Color
                        <input
                          type="color"
                          value={portalOverlayColor}
                          onChange={(e) => setPortalOverlayColor(e.target.value)}
                          style={{ width: 36, height: 28, border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer', padding: 0 }}
                        />
                        <code style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{portalOverlayColor}</code>
                      </label>
                      <p className="setting-hint">
                        Background overlay color that sits on top of the background image.
                      </p>
                    </div>

                    {/* Overlay Opacity */}
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        Overlay Opacity: <strong>{Math.round(portalOverlayOpacity * 100)}%</strong>
                      </label>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={Math.round(portalOverlayOpacity * 100)}
                        onChange={(e) => setPortalOverlayOpacity(Number(e.target.value) / 100)}
                        style={{ width: '100%' }}
                      />
                      <p className="setting-hint">
                        0% = fully transparent (background image visible), 100% = fully opaque.
                      </p>
                    </div>

                    {/* Portal Background Image Override */}
                    <div>
                      <label style={{ marginBottom: 4, display: 'block' }}>Portal Background Image (Default)</label>
                      <p className="setting-hint" style={{ marginTop: 0 }}>
                        Default background image for all portals. Individual portals can override this from their settings.
                      </p>
                      {portalBackgroundImage ? (
                        <div className="selected-media-preview">
                          <img src={portalBackgroundImage.url} alt={portalBackgroundImage.name} className="media-thumbnail" />
                          <span className="media-name">{portalBackgroundImage.name}</span>
                          <button
                            className="remove-media-btn"
                            onClick={() => setPortalBackgroundImage(null)}
                            title="Remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          className="select-media-btn"
                          onClick={() => setShowPortalBgPicker(true)}
                        >
                          <Upload size={16} />
                          Select Background Image
                        </button>
                      )}
                    </div>

                    {/* Preview */}
                    <div style={{ marginTop: 4 }}>
                      <label style={{ marginBottom: 8, display: 'block' }}>Preview</label>
                      <div style={{
                        position: 'relative',
                        width: '100%',
                        height: 80,
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid var(--color-border)',
                      }}>
                        {(portalBackgroundImage || engagementBackground) && (
                          <div style={{
                            position: 'absolute',
                            inset: 0,
                            backgroundImage: `url(${portalBackgroundImage?.url || engagementBackground?.url})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            filter: 'blur(8px)',
                            transform: 'scale(1.1)',
                          }} />
                        )}
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          background: portalOverlayColor,
                          opacity: portalOverlayOpacity,
                        }} />
                        <div style={{
                          position: 'relative',
                          zIndex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          color: 'var(--pm-accent)',
                          fontWeight: 500,
                          fontSize: 13,
                        }}>
                          Portal Background Preview
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="setting-divider"></div>

                <p className="setting-description" style={{ marginTop: 4, marginBottom: 12 }}>
                  Use <strong>Save Docs Settings</strong> below to save logo, background, payment links, and client PDFs.
                  The footer <strong>Save Changes</strong> button does not update this tab (so incomplete loads cannot clear your documents).
                </p>

                {/* Save Button */}
                <button
                  className="save-button"
                  onClick={async () => {
                    setSavingDocsSettings(true);
                    try {
                      const [
                        resolvedLogo,
                        resolvedBackground,
                        resolvedPortalBg,
                        resolvedSamplePlans,
                      ] = await Promise.all([
                        ensurePortalPublicBrandingMediaItem(engagementLogo),
                        ensurePortalPublicBrandingMediaItem(engagementBackground),
                        ensurePortalPublicBrandingMediaItem(portalBackgroundImage),
                        ensurePortalPublicBrandingMediaItem(samplePlansImage),
                      ]);
                      if (resolvedLogo !== engagementLogo) setEngagementLogo(resolvedLogo);
                      if (resolvedBackground !== engagementBackground) setEngagementBackground(resolvedBackground);
                      if (resolvedPortalBg !== portalBackgroundImage) setPortalBackgroundImage(resolvedPortalBg);
                      if (resolvedSamplePlans !== samplePlansImage) setSamplePlansImage(resolvedSamplePlans);

                      console.log('Saving docs settings:', {
                        engagementLogo: resolvedLogo,
                        engagementBackground: resolvedBackground,
                        engagement3DModel,
                        engagementDocuments
                      });

                      // Save all docs settings at once using setDoc with merge
                      const settingsRef = doc(db, 'settings', 'app_settings');
                      const docsSettings: Record<string, any> = {
                        engagementLogo: resolvedLogo || null,
                        engagementBackground: resolvedBackground || null,
                        engagement3DModel: engagement3DModel || null,
                        samplePlansImage: resolvedSamplePlans || null,
                        engagementDocuments: engagementDocuments || [],
                        engagementVideoUrl: engagementVideoUrl || null,
                        retainerTotalFeeRange: retainerTotalFeeRange || '$4,500 - $6,000',
                        conceptPaymentLinkId: conceptPaymentLinkId || null,
                        retainerPaymentLinkId: retainerPaymentLinkId || null,
                        portalOverlayColor: portalOverlayColor,
                        portalOverlayOpacity: portalOverlayOpacity,
                        portalBackgroundImage: resolvedPortalBg || null,
                        updatedAt: new Date(),
                      };

                      console.log('Writing to Firestore:', docsSettings);
                      await setDoc(settingsRef, docsSettings, { merge: true });
                      await mergePublicSettingsSubset(docsSettings);

                      console.log('Docs settings saved successfully');
                      alert('Docs settings saved successfully!');
                    } catch (error) {
                      console.error('Error saving docs settings:', error);
                      alert('Failed to save settings: ' + (error instanceof Error ? error.message : 'Unknown error'));
                    } finally {
                      setSavingDocsSettings(false);
                    }
                  }}
                  disabled={savingDocsSettings}
                >
                  {savingDocsSettings ? 'Saving...' : 'Save Docs Settings'}
                </button>
                </section>

                <section className="settings-ops-section" aria-label="Raise jobs board">
                  <CrewBoardSettingsCard />
                </section>

                <section className="settings-ops-section" aria-label="Design Questionnaire">
                  <h4 className="settings-ops-section-title">Design Questionnaire</h4>
                  <QuestionnaireSettingsTab />
                </section>

                <section className="settings-ops-section" aria-label="Client Portal Branding">
                  <h4 className="settings-ops-section-title">Client Portal Branding</h4>
                  <p className="setting-description">
                  Configure the design fee services and pricing shown to clients on the portal.
                </p>

                {feeServices.map((svc, idx) => (
                  <div key={svc.id} style={{ padding: '16px', background: 'var(--color-surface)', borderRadius: 8, marginBottom: 12, border: '1px solid var(--color-border)' }}>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Service Name</label>
                        <input
                          type="text"
                          value={svc.name}
                          onChange={e => {
                            const updated = [...feeServices];
                            updated[idx] = { ...svc, name: e.target.value };
                            setFeeServices(updated);
                          }}
                          style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14 }}
                        />
                      </div>
                      <div style={{ width: 120 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Price ($)</label>
                        <input
                          type="number"
                          value={svc.price}
                          onChange={e => {
                            const updated = [...feeServices];
                            updated[idx] = { ...svc, price: Number(e.target.value) };
                            setFeeServices(updated);
                          }}
                          style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14 }}
                        />
                      </div>
                      {svc.required && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pm-accent)', background: 'var(--color-primary-subtle)', padding: '2px 8px', borderRadius: 8 }}>Required</span>}
                    </div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Description</label>
                    <input
                      type="text"
                      value={svc.description}
                      onChange={e => {
                        const updated = [...feeServices];
                        updated[idx] = { ...svc, description: e.target.value };
                        setFeeServices(updated);
                      }}
                      style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14 }}
                    />
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button
                    className="save-button"
                    disabled={savingFees}
                    onClick={async () => {
                      setSavingFees(true);
                      setFeeSaveStatus('idle');
                      try {
                        await setDoc(doc(db, 'appSettings', 'designFeePricing'), { services: feeServices }, { merge: true });
                        setFeeSaveStatus('saved');
                        setTimeout(() => setFeeSaveStatus('idle'), 2000);
                      } catch (err) {
                        console.error('Error saving fee pricing:', err);
                        setFeeSaveStatus('error');
                      } finally {
                        setSavingFees(false);
                      }
                    }}
                  >
                    {savingFees ? 'Saving...' : feeSaveStatus === 'saved' ? '\u2713 Saved' : 'Save Pricing'}
                  </button>
                </div>

                <div className="setting-divider" style={{ margin: '24px 0' }}></div>

                <section className="settings-ops-section" aria-label="Beta features">
                  <h4 className="settings-ops-section-title">Beta features</h4>
                  <div className="setting-item">
                    <label className="cw-toggle-row" data-on={modelWalkthroughDefaultEnabled} style={{ marginTop: 0 }}>
                      <input
                        type="checkbox"
                        checked={modelWalkthroughDefaultEnabled}
                        disabled={savingWalkthroughDefault}
                        onChange={async e => {
                          const next = e.target.checked;
                          setModelWalkthroughDefaultEnabled(next);
                          setSavingWalkthroughDefault(true);
                          try {
                            await setDoc(
                              doc(db, 'settings', 'app_settings'),
                              { modelWalkthroughDefaultEnabled: next, updatedAt: new Date() },
                              { merge: true },
                            );
                          } catch (err) {
                            console.error('Failed to save walkthrough default', err);
                            setModelWalkthroughDefaultEnabled(!next);
                          } finally {
                            setSavingWalkthroughDefault(false);
                          }
                        }}
                      />
                      <span>
                        3D walkthrough recorder — enabled by default for new projects{' '}
                        <span className="pmwr-beta">Beta</span>
                      </span>
                    </label>
                    <p className="setting-hint">
                      Off by default. Employees can still enable per project in Client Portal Management.
                    </p>
                  </div>
                  <div className="setting-item">
                    <label className="cw-toggle-row" data-on={phasedProjectsDefaultEnabled} style={{ marginTop: 0 }}>
                      <input
                        type="checkbox"
                        checked={phasedProjectsDefaultEnabled}
                        disabled={savingPhasedProjectsDefault}
                        onChange={async e => {
                          const next = e.target.checked;
                          setPhasedProjectsDefaultEnabled(next);
                          setSavingPhasedProjectsDefault(true);
                          try {
                            await setDoc(
                              doc(db, 'settings', 'app_settings'),
                              { phasedProjectsDefaultEnabled: next, updatedAt: new Date() },
                              { merge: true },
                            );
                          } catch (err) {
                            console.error('Failed to save phased projects default', err);
                            setPhasedProjectsDefaultEnabled(!next);
                          } finally {
                            setSavingPhasedProjectsDefault(false);
                          }
                        }}
                      />
                      <span>
                        Phased project structures — show the phasing toggle in the proposal builder{' '}
                        <span className="pmwr-beta">Beta</span>
                      </span>
                    </label>
                    <p className="setting-hint">
                      Off by default. Proposals that already have phasing enabled stay manageable regardless.
                    </p>
                  </div>
                </section>

                <div className="setting-divider" style={{ margin: '24px 0' }}></div>

                <h3>Sample 3D Model</h3>
                <p className="setting-description">
                  Upload an HTML file with a sample 3D model viewer. Clients can preview this from the design fee step on their portal.
                </p>
                <div className="setting-item">
                  {engagement3DModel ? (
                    <div className="selected-media-preview">
                      <span className="media-icon">📦</span>
                      <span className="media-name">{engagement3DModel.name}</span>
                      <button
                        className="remove-media-btn"
                        onClick={async () => {
                          setEngagement3DModel(null);
                          try {
                            await setDoc(doc(db, 'settings', 'app_settings'), { engagement3DModel: null }, { merge: true });
                          } catch (err) { console.error('Error removing 3D model:', err); }
                        }}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      className="select-media-btn"
                      onClick={() => setShowModelPicker(true)}
                    >
                      <Upload size={16} />
                      Select 3D Model HTML
                    </button>
                  )}
                  <p className="setting-hint">
                    Upload an HTML file containing your 3D model viewer (e.g., from SketchUp or Sketchfab export).
                  </p>
                </div>
                </section>
              </div>
            )}

            {section === 'project-defaults' && (
              <div className="settings-section">
                <h3>Project Defaults</h3>
                <section className="settings-ops-section" aria-label="Timeline Phases">
                  <h4 className="settings-ops-section-title">Timeline Phases</h4>
                  <p className="setting-description">
                  Configure the default phase durations shown on the "If You Started Today" timeline in the client portal. These can be overridden per-project.
                </p>

                {timelinePhases.map((phase, idx) => {
                  const hasSubPhases = phase.subPhases && phase.subPhases.length > 0;
                  const isProduction = phase.id === 'in-production';
                  return (
                    <div key={phase.id} style={{ padding: '16px', background: 'var(--color-surface)', borderRadius: 8, marginBottom: 12, border: '1px solid var(--color-border)' }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        {isProduction && (
                          <button
                            onClick={() => setExpandedProductionRow(!expandedProductionRow)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center' }}
                          >
                            {expandedProductionRow ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        )}
                        <div style={{ flex: 1, minWidth: 140 }}>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Phase</label>
                          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text-primary)' }}>{phase.label}</span>
                        </div>
                        <div style={{ width: 110 }}>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Duration (wks)</label>
                          <input
                            type="number"
                            min={1}
                            value={phase.durationWeeks}
                            disabled={hasSubPhases}
                            onChange={e => {
                              const updated = [...timelinePhases];
                              updated[idx] = { ...phase, durationWeeks: Math.max(1, Number(e.target.value)) };
                              setTimelinePhases(updated);
                            }}
                            style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14, background: hasSubPhases ? '#F3F4F6' : 'white' }}
                          />
                          {hasSubPhases && (
                            <span style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2, display: 'block' }}>Auto-summed</span>
                          )}
                        </div>
                        <div style={{ width: 110 }}>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Buffer (wks)</label>
                          <input
                            type="number"
                            min={0}
                            value={phase.bufferWeeks}
                            onChange={e => {
                              const updated = [...timelinePhases];
                              updated[idx] = { ...phase, bufferWeeks: Math.max(0, Number(e.target.value)) };
                              setTimelinePhases(updated);
                            }}
                            style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14 }}
                          />
                        </div>
                      </div>

                      {/* Sub-phases for Production */}
                      {isProduction && expandedProductionRow && phase.subPhases && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--color-border)' }}>
                          {phase.subPhases.map((sub, sIdx) => (
                            <div key={sub.id} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: sIdx < phase.subPhases!.length - 1 ? 8 : 0, paddingLeft: 28 }}>
                              <div style={{ flex: 1, minWidth: 120 }}>
                                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{sub.label}</span>
                              </div>
                              <div style={{ width: 110 }}>
                                <input
                                  type="number"
                                  min={1}
                                  value={sub.durationWeeks}
                                  onChange={e => {
                                    const newWeeks = Math.max(1, Number(e.target.value));
                                    const updatedPhases = [...timelinePhases];
                                    const updatedSubs = [...phase.subPhases!];
                                    updatedSubs[sIdx] = { ...sub, durationWeeks: newWeeks };
                                    const totalWeeks = updatedSubs.reduce((sum, s) => sum + s.durationWeeks, 0);
                                    updatedPhases[idx] = { ...phase, subPhases: updatedSubs, durationWeeks: totalWeeks };
                                    setTimelinePhases(updatedPhases);
                                  }}
                                  style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14 }}
                                />
                              </div>
                              <div style={{ width: 110 }} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Preview */}
                <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--color-primary-subtle)', borderRadius: 8, border: '1px solid var(--color-success)' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--pm-accent)', marginBottom: 6 }}>Preview — Total Project Duration</div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
                    {(() => {
                      const totalWeeks = timelinePhases.reduce((sum, p) => sum + p.durationWeeks + p.bufferWeeks, 0);
                      const breakdown = timelinePhases.map(p => {
                        const parts = [p.label, `${p.durationWeeks}wk`];
                        if (p.bufferWeeks > 0) parts.push(`+ ${p.bufferWeeks}wk buffer`);
                        return parts.join(' ');
                      }).join('  →  ');
                      return (
                        <>
                          <strong>~{totalWeeks} weeks</strong> from signing to completion
                          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>{breakdown}</div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button
                    className="save-button"
                    disabled={savingTimeline}
                    onClick={async () => {
                      setSavingTimeline(true);
                      setTimelineSaveStatus('idle');
                      try {
                        await updateSetting('projectTimelineDefaults', { phases: timelinePhases });
                        setTimelineSaveStatus('saved');
                        setTimeout(() => setTimelineSaveStatus('idle'), 2000);
                      } catch (err) {
                        console.error('Error saving timeline defaults:', err);
                        setTimelineSaveStatus('error');
                      } finally {
                        setSavingTimeline(false);
                      }
                    }}
                  >
                    {savingTimeline ? 'Saving...' : timelineSaveStatus === 'saved' ? '\u2713 Saved' : 'Save Timeline Defaults'}
                  </button>
                </div>
                </section>

                <GroupTimelineColorsSettingsCard />

                <PaymentTermsSettingsCard />

                <PurchasingPolicySettingsCard />

                {/* TEMPORARY pilot gate for the Files redesign — see REMOVAL CHECKLIST in utils/filesRedesignAccess.ts. */}
                <FilesRedesignPilotCard />

                {/* RFQ request rounds (Files → RFQs "Requests" view) — pilot gate. */}
                <RfqRequestsPilotCard />

                <DesignerStandardsSettingsCard />

                <section className="settings-ops-section" aria-label="Onboarding Checklist">
                  <h4 className="settings-ops-section-title">Onboarding Checklist</h4>
                  <p className="setting-description">
                  Define the checklist that is auto-generated when a project enters the "Under Contract" phase. Items are assigned to team members who receive notifications as the list progresses.
                </p>

                {onboardingItems.map((item, idx) => (
                  <div key={item.id} style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start', padding: '14px 16px',
                    background: 'var(--color-surface)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--color-border)',
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 6, color: 'var(--color-text-muted)' }}>
                      <GripVertical size={14} />
                      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-secondary)', textAlign: 'center', lineHeight: 1 }}>{idx + 1}</span>
                    </div>

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          type="text"
                          value={item.title}
                          placeholder="Task title"
                          onChange={e => {
                            const updated = [...onboardingItems];
                            updated[idx] = { ...item, title: e.target.value };
                            setOnboardingItems(updated);
                          }}
                          style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14 }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="text"
                          value={item.description || ''}
                          placeholder="Optional description / guidance"
                          onChange={e => {
                            const updated = [...onboardingItems];
                            updated[idx] = { ...item, description: e.target.value };
                            setOnboardingItems(updated);
                          }}
                          style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, color: 'var(--color-text-secondary)' }}
                        />
                        <select
                          value={item.department || 'design'}
                          onChange={e => {
                            const updated = [...onboardingItems];
                            updated[idx] = { ...item, department: e.target.value as ProjectDepartment };
                            setOnboardingItems(updated);
                          }}
                          title="Department (task table grouping)"
                          style={{ width: 130, padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13 }}
                        >
                          {CUSTOM_FRAME_DEPARTMENTS.map((d) => (
                            <option key={d} value={d}>{DEPARTMENT_DISPLAY_LABELS[d]}</option>
                          ))}
                        </select>
                        <select
                          value={item.defaultAssigneeId || ''}
                          onChange={e => {
                            const updated = [...onboardingItems];
                            updated[idx] = { ...item, defaultAssigneeId: e.target.value || undefined };
                            setOnboardingItems(updated);
                          }}
                          style={{ width: 160, padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13 }}
                        >
                          <option value="">Unassigned</option>
                          {crmUsers.filter(u => u.isActive).map(u => (
                            <option key={u.uid} value={u.uid}>{u.displayName || u.email}</option>
                          ))}
                        </select>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={item.required}
                            onChange={e => {
                              const updated = [...onboardingItems];
                              updated[idx] = { ...item, required: e.target.checked };
                              setOnboardingItems(updated);
                            }}
                          />
                          Required
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!item.onCriticalPath}
                            onChange={e => {
                              const updated = [...onboardingItems];
                              updated[idx] = { ...item, onCriticalPath: e.target.checked };
                              setOnboardingItems(updated);
                            }}
                          />
                          Critical path
                        </label>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 4 }}>
                      <button
                        disabled={idx === 0}
                        onClick={() => {
                          const updated = [...onboardingItems];
                          [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
                          updated.forEach((it, i) => it.order = i);
                          setOnboardingItems(updated);
                        }}
                        style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', padding: 2, color: idx === 0 ? '#D1D5DB' : '#6B7280' }}
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        disabled={idx === onboardingItems.length - 1}
                        onClick={() => {
                          const updated = [...onboardingItems];
                          [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
                          updated.forEach((it, i) => it.order = i);
                          setOnboardingItems(updated);
                        }}
                        style={{ background: 'none', border: 'none', cursor: idx === onboardingItems.length - 1 ? 'default' : 'pointer', padding: 2, color: idx === onboardingItems.length - 1 ? '#D1D5DB' : '#6B7280' }}
                      >
                        <ArrowDown size={14} />
                      </button>
                      <button
                        onClick={() => setOnboardingItems(onboardingItems.filter((_, i) => i !== idx))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-error-text)', marginTop: 2 }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}

                {onboardingItems.length === 0 && (
                  <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 14, background: 'var(--color-surface)', borderRadius: 8, border: '1px dashed var(--color-border)' }}>
                    No checklist items yet. Add your first item below.
                  </div>
                )}

                <button
                  onClick={() => {
                    const newItem: OnboardingTemplateItem = {
                      id: `ob-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                      title: '',
                      category: 'Admin',
                      department: 'design',
                      order: onboardingItems.length,
                      required: true,
                      onCriticalPath: false,
                    };
                    setOnboardingItems([...onboardingItems, newItem]);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', marginTop: 8,
                    background: 'none', border: '1px dashed #9CA3AF', borderRadius: 6, cursor: 'pointer',
                    color: 'var(--color-text-secondary)', fontSize: 13, fontWeight: 500,
                  }}
                >
                  <Plus size={14} /> Add Checklist Item
                </button>

                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                  <button
                    className="save-button"
                    disabled={savingOnboarding}
                    onClick={async () => {
                      setSavingOnboarding(true);
                      setOnboardingSaveStatus('idle');
                      try {
                        const cleanedItems = onboardingItems
                          .filter(it => it.title.trim())
                          .map((it, i) => ({
                            ...it,
                            order: i,
                            department: it.department || 'design',
                          }));
                        await setDoc(doc(db, 'settings', 'onboardingChecklist'), {
                          items: cleanedItems,
                          updatedAt: new Date(),
                        });
                        setOnboardingItems(cleanedItems);
                        setOnboardingSaveStatus('saved');
                        setTimeout(() => setOnboardingSaveStatus('idle'), 2000);
                      } catch (err) {
                        console.error('Error saving onboarding template:', err);
                        setOnboardingSaveStatus('error');
                      } finally {
                        setSavingOnboarding(false);
                      }
                    }}
                  >
                    {savingOnboarding ? 'Saving...' : onboardingSaveStatus === 'saved' ? '\u2713 Saved' : 'Save Onboarding Template'}
                  </button>
                  {onboardingSaveStatus === 'error' && (
                    <span style={{ color: 'var(--color-error-text)', fontSize: 13, alignSelf: 'center' }}>Failed to save</span>
                  )}
                </div>
                </section>

                <section className="settings-ops-section" aria-label="Project Numbering">
                  <h4 className="settings-ops-section-title">Project Numbering</h4>
                  <ProjectNumberingSettings />
                </section>
              </div>
            )}

            {/* Media Pickers — Client Experience branding / documents (state lives on this hub) */}
            <MediaPicker
              isOpen={showLogoPicker}
              onClose={() => setShowLogoPicker(false)}
              onSelect={(item: MediaItem) => {
                setEngagementLogo({ url: item.url, name: item.name });
                setShowLogoPicker(false);
              }}
              allowedTypes={['image']}
              title="Select Logo"
            />
            <MediaPicker
              isOpen={showBackgroundPicker}
              onClose={() => setShowBackgroundPicker(false)}
              onSelect={(item: MediaItem) => {
                setEngagementBackground({ url: item.url, name: item.name });
                setShowBackgroundPicker(false);
              }}
              allowedTypes={['image']}
              title="Select Background Image"
            />
            <MediaPicker
              isOpen={showModelPicker}
              onClose={() => setShowModelPicker(false)}
              onSelect={async (item: MediaItem) => {
                const model = { url: item.url, name: item.name };
                setEngagement3DModel(model);
                setShowModelPicker(false);
                try {
                  await setDoc(doc(db, 'settings', 'app_settings'), { engagement3DModel: model }, { merge: true });
                } catch (err) {
                  console.error('Error saving 3D model:', err);
                }
              }}
              allowedTypes={['document']}
              title="Select 3D Model HTML"
            />
            <MediaPicker
              isOpen={showSamplePlansPicker}
              onClose={() => setShowSamplePlansPicker(false)}
              onSelect={(item: MediaItem) => {
                setSamplePlansImage({ url: item.url, name: item.name });
                setShowSamplePlansPicker(false);
              }}
              allowedTypes={['image']}
              title="Select Sample Plans Image"
            />
            <MediaPicker
              isOpen={showDocPicker}
              onClose={() => setShowDocPicker(false)}
              onSelect={(item: MediaItem) => {
                setEngagementDocuments(prev => [...prev, {
                  id: item.id || Date.now().toString(),
                  name: item.name,
                  url: item.url,
                }]);
                setShowDocPicker(false);
              }}
              allowedTypes={['document']}
              title="Select Document"
            />
            <MediaPicker
              isOpen={showPortalBgPicker}
              onClose={() => setShowPortalBgPicker(false)}
              onSelect={(item: MediaItem) => {
                setPortalBackgroundImage({ url: item.url, name: item.name });
                setShowPortalBgPicker(false);
              }}
              allowedTypes={['image']}
              title="Select Portal Background Image"
            />
    </div>
  );
};

// CSS for readonly values
const styles = `
  .readonly-value {
    padding: 8px 12px;
    background: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.9rem;
    color: #666;
    word-break: break-all;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

export default SettingsHubSection;
